import type { MetaState, ThreatLevelId } from "../core/MetaState.ts";
import { DEFAULT_THREAT_LEVEL, THREAT_LEVEL_IDS } from "../core/MetaState.ts";
import type { OperatorId, ModuleId } from "../core/RunState.ts";
import type { EventBus } from "../core/EventBus.ts";
import { sanitizeInitials } from "../core/initials.ts";
import { getOperatorUnlockThreshold } from "../systems/OperatorEffects.ts";
import { getAllModules } from "../systems/ModuleRegistry.ts";
import { getPreRunStyles } from "./PreRunScreenStyles.ts";
import { t } from "../utils/i18n.ts";
import {
  OPERATOR_NAMES,
  OPERATOR_VERSIONS,
  OPERATOR_DESC,
  OPERATOR_RARITY,
  totalRunsFrom,
  threatLockReason,
  threatRunsRemaining,
} from "./PreRunScreenData.ts";
import type { AIModule } from "../systems/ModuleRegistry.ts";
import { buildNameInput, readCellChar } from "./PreRunScreenInput.ts";
import { crumb, crumbFrames } from "../platform/diagnostics.ts";
import { UI_CHROME } from "./uiTokens.ts";
import { COLORS } from "../core/constants.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import { SUBMIT_PRIVACY_NOTICE } from "../legal/submitPrivacyNotice.ts";
import { getLocale } from "../utils/i18n.ts";
import { gearMenuStyles } from "./GearMenu.ts";
import {
  ModalShell,
  modalButton,
  modalFooter,
  modalHeader,
  modalRow,
  modalSection,
} from "./modalShell.ts";
import { InfoTooltip } from "./InfoTooltip.ts";

export interface PreRunSelection {
  operator: OperatorId;
  modules: ModuleId[];
  // Task-002 (2026-threat-profile-model, AC-8/AC-12) — carried, never applied
  // (§6 forbids unused placeholder mechanics; Phase 1 ships no selector, so
  // this is always the persisted default). Read from `metaState.settings` at
  // deploy time, below — not user-selectable here.
  threatLevel: ThreatLevelId;
}

export type PreRunCallback = (selection: PreRunSelection) => void;
export type PreRunBackCallback = () => void;

const ALL_OPERATORS: readonly OperatorId[] = [
  "sentinel-7",
  "warden",
  "ghost",
  "architect",
  "cipher",
];

// Exported so tests can verify against the REAL rarity palette (e.g. the
// AC-17 depth-contrast check) instead of a hand-copied duplicate that could
// silently drift from this table.
export const RARITY_COLORS: Record<string, string> = {
  green: COLORS.SENTINEL_GREEN,
  cyan: COLORS.ICE_CYAN,
  gold: UI_CHROME.etchAccent,
  violet: "#9A6BFF",
};

/**
 * Richtung C (`docs/prerun_c_fullscreen_2026-08.html`, tier table at :848-864)
 * — each threat tier owns a COLOUR, and the five together are the escalation:
 * cyan → amber → rose → ECHELON signal → ECHELON deep. Before this the whole
 * rail was one mint (`.prerun-tr-tile.sel` read `greenHi`), so the escalation
 * was mute in the UI while the ART carried it — the gap analysis's §H(3),
 * `docs/prerun_c_gap_2026-08.md`.
 *
 * Every value resolves from the central palette; no new gameplay hex. The
 * split at tier 3/4 is D-224's own line and it lands where the fiction does:
 * LOCKDOWN is the mainframe sealing ITSELF, so it takes `ORACLE_RED` (your own
 * alarm state — and the rose the threat panel already ships). OVERRUN and
 * FALLOUT are the two tiers where containment has failed, so they take
 * ECHELON's family — her voice, not yours.
 */
const THREAT_TIER_COLOR: Record<ThreatLevelId, string> = {
  sandbox: COLORS.ICE_CYAN,
  containment: COLORS.AMBER_WARN,
  lockdown: COLORS.ORACLE_RED,
  overrun: ECHELON.SIGNAL,
  fallout: ECHELON.DEEP,
};

/** Per-operator class crest (the small glyph beside the name in the hero's
 *  operator identity block), tinted by rarity in CSS. */
const OPERATOR_CREST: Record<OperatorId, string> = {
  "sentinel-7": "⛨",
  warden: "⬡",
  ghost: "◈",
  architect: "❖",
  cipher: "✶",
};

/** Display CATEGORY for a module, derived from its effect type (UI-only — the
 *  module data/JSON is unchanged). Falls back to SYSTEM for anything unmapped. */
const MODULE_CATEGORY: Record<string, string> = {
  starting_credits: "economy",
  free_first_tower: "economy",
  range_multiplier: "combat",
  boss_damage_multiplier: "combat",
  score_multiplier: "analytics",
  synergy_multiplier: "analytics",
  draft_draw_count: "analytics",
  revive: "defense",
  extra_hp: "defense",
  combo_timeout_multiplier: "adaptive",
  auto_degrade_threshold: "adaptive",
  skill_slots: "system",
};

/** AC-30 (KEEP, re-scoped by the delta to rotary positions) — the crop
 *  anchor per tier. The rail tiles are gone with the single-screen
 *  architecture; the rule they carried is not. Task-003's original note
 *  follows.
 *
 *  Task-003 (2026-prestart-deployment-redesign, AC-30) — the rail-tile crop's
 *  vertical anchor, `docs/THREAT_ART_BIBLE.md` §7's `object-position: 50%
 *  <Y>%`, keyed per tier so a future per-tier deviation has a documented home
 *  right next to the tier it adjusts (the bible explicitly allows this, but
 *  requires it live here, not there). All five currently share the bible's
 *  default 22% — no deviation has been needed yet. */
const RAIL_CROP_Y: Record<ThreatLevelId, number> = {
  sandbox: 22,
  containment: 22,
  lockdown: 22,
  overrun: 22,
  fallout: 22,
};

/**
 * Pre-Run Screen — v5 redesign: lit-CRT panels + VERA⇄FENRIR hero + gated deploy.
 * Uses uiTokens, BoardChrome vocabulary, palette.ts colours. No bespoke chrome.
 */
export class PreRunScreen {
  private overlay: HTMLDivElement;
  private onStart: PreRunCallback | null = null;
  private onBack: PreRunBackCallback | null = null;
  private onDeployStart: (() => void) | null = null;
  private onReset: (() => void | Promise<void>) | null = null;
  // BL-115 — the runtime locale switch. `onSetLanguage` persists + applies the
  // chosen locale (wired in main.ts); `metaStateRef` lets the screen re-render
  // itself in the new language on toggle.
  private onSetLanguage: ((lang: "en" | "de") => void) | null = null;
  private metaStateRef: MetaState | null = null;
  private resetArmTimer: number | null = null;
  private selectedOperator: OperatorId = "sentinel-7";
  // BL-103: pick up to 3 AI Modules. Defaults to [] — a no-pick run is
  // byte-identical to the pre-grid behaviour (the determinism guardrail).
  private selectedModules: ModuleId[] = [];
  private deployBtn: HTMLButtonElement | null = null;
  private deployHint: HTMLElement | null = null;
  // S188/BL-209 — the SELECT OPERATOR swipe carousel (owner's ChatGPT ref): the
  // CENTERED card is the live selection; the previous/next variants peek smaller
  // at the sides. `focusIndex` is the centered operator; the readiness gate bar
  // fills as the deploy steps complete.
  private focusIndex = 0;
  /** Richtung C — the live hero nodes. The operator is no longer a card in a
   *  sliding track but a FIGURE on the shared stage plus an identity block and
   *  a stepper, so what has to be kept live is the figure's image, the block's
   *  text nodes and the pips. Held as fields so the re-render works while the
   *  stage is still detached during build. */
  /** 2026-prestart-two-screen-delta — which act is mounted (D-AC-1 / D-AC-2),
   *  and the stage both acts play on. */
  private act: 1 | 2 = 1;
  /** The Operator-ID as last seen in act 1's cells. D-AC-15: the step to act 2
   *  must be lossless, and act 2 mounts no cells to read — see
   *  `getInitialsValue`. Seeded from the persisted save so a returning player
   *  keeps their ID, then kept current by every edit and by the act swap. */
  private committedInitials = "";
  private mainEl: HTMLElement | null = null;
  /** The rotary's live nodes — one per option, plus the jump-target pips. Both
   *  acts build them through `buildHeroStage`, so there is exactly one set at
   *  a time and exactly one code path that positions them (`applyRotary`). */
  private rotaryItems: HTMLElement[] = [];
  private rotaryPips: HTMLElement[] = [];
  private rotaryArrows: HTMLElement[] = [];
  /** The hero caption, which lives OUTSIDE the protection zone (D-AC-4). */
  private heroCapName: HTMLElement | null = null;
  private heroCapRole: HTMLElement | null = null;
  private heroCapFx: HTMLElement | null = null;
  private heroCapLock: HTMLElement | null = null;
  /** Act 2's read-only mirror of what act 1 committed (D-AC-15). */
  private summaryEls: HTMLElement[] = [];
  /** Act 1's primary action (D-AC-12) — WEITER, which is NOT the deploy gate
   *  (D-AC-14(a)); it only needs a playable operator committed. */
  private advanceBtn: HTMLButtonElement | null = null;
  private advanceHint: HTMLElement | null = null;
  /** Act 2's threat-context surface (D-AC-2 zone 3). */
  // BL-218 — the on-stage operator may be a LOCKED variant (browsable for
  // preview). `focusedLocked` drives the Deploy gate (deploy is disabled while a
  // locked figure is on stage); `focusedOperator` is that id (locked or not)
  // so the gate hint can name its unlock threshold. `selectedOperator` stays the
  // last UNLOCKED pick — the only thing that can actually deploy.
  private focusedLocked = false;
  private focusedOperator: OperatorId = "sentinel-7";
  /** True for the click that a real swipe synthesises on the card under the
   *  finger — so the snap wins and a stray tap doesn't re-centre (BL-218). Reset
   *  at the start of every pointer interaction so it can never stick. */
  private suppressCardClick = false;
  // Richtung C — the readiness BUS that replaced Task-007's `.prerun-gatebar`
  // fill: three nodes (one per gate condition) and the traces between them,
  // plus the ONE line that names the break. See `buildReadinessBus`.
  private busNodes: HTMLElement[] = [];
  private busTraces: HTMLElement[] = [];
  private needLine: HTMLElement | null = null;
  // BL-200 — the Deploy-button readiness checklist tooltip (hover/focus/touch).
  // Reuses the shared on-stage-clamped, wrap-guarded InfoTooltip card.
  private readonly deployTip = new InfoTooltip();
  private deployTipOpen = false;
  // Task-005 (2026-prestart-deployment-redesign, AC-6/AC-22) — the compact
  // Operator-ID chip and the setter surface it opens. The chip's value node
  // is kept live so it can be refreshed on every keystroke/tap
  // (`renderIdChip`); the setter is the AC-1(e) disclosure surface
  // (`data-surface="id-setter"` on its modal root), mutually exclusive with
  // the module catalog (`openIdSetter`/`openModuleCatalog`). DEVICE ROUND
  // 2026-08-18 (owner): the setter is a CENTERED MODAL on the shared shell
  // (same system as the in-game Einstellungen/Pause), no longer a drop-up.
  private idChip: HTMLElement | null = null;
  private idChipValue: HTMLElement | null = null;
  private modCountEl: HTMLElement | null = null;
  private idModal: ModalShell | null = null;
  private nameField: HTMLElement | null = null;
  private grantName: HTMLElement | null = null;
  private destroyed = false;
  private escHandler: ((e: KeyboardEvent) => void) | null = null;
  private readonly bus: EventBus | null;
  private musicMuted = false;
  private sfxMuted = false;
  private muteUnsubs: Array<() => void> = [];
  /** S129/D-227 — the in-app legal/privacy surface, opened from the top bar. */
  /** B31 (2026-08-15): siehe BootSequence — Klasse erst beim Oeffnen. */
  private legalOverlay: import("./LegalOverlay.ts").LegalOverlay | null = null;
  /** BL-130's ONE ⚙ (top-right) stays — but per the DEVICE ROUND 2026-08-18
   *  (owner) it opens the shared CENTERED settings modal instead of a
   *  dropdown, mirroring what spec `2026-pause-settings-overlays` already did
   *  to the in-game gear. Rebuilt each act render; torn down on `hide()`. */
  private settingsModal: ModalShell | null = null;
  // Task-001 (2026-prestart-deployment-redesign, AC-3) — the three console
  // loadout-slot tiles (index-aligned with `selectedModules`) and the module
  // catalog they disclose. An empty slot is the ONLY entry into the catalog.
  // DEVICE ROUND 2026-08-18 (owner): the catalog is a CENTERED MODAL on the
  // shared shell — open/closed is read straight off the shell (`visible`),
  // one source, so nothing can disagree with it.
  private slotEls: HTMLElement[] = [];
  private moduleModal: ModalShell | null = null;
  // Task-003 (2026-prestart-deployment-redesign, AC-10/AC-15) — the threat
  // rail. Mirrors the operator carousel's focus/commit split (focusIndex/
  // focusedOperator/selectedOperator above): `threatFocusIndex` is the
  // currently INSPECTED tier (may be locked, browsable); `selectedThreatLevel`
  // is the last UNLOCKED tier the player landed on — the only one that can
  // deploy (AC-10: "the gate blocks the commit, not the tap"). Unlike the
  // carousel, the rail has no sliding track — AC-11 requires all five tiles
  // on-screen at once, so `threatRailEls` is a flat, never-reordered list
  // (AC-15) rather than a track queried live.
  private threatFocusIndex = 0;
  private selectedThreatLevel: ThreatLevelId = DEFAULT_THREAT_LEVEL;
  /** The hero threat portrait (`data-hero="threat"`) — kept live so it can
   *  track the rail's focus (AC-30: same source as the focused rail tile). */
  /** True for the click a real rail swipe synthesises on the tile under the
   *  finger — mirrors `suppressCardClick` so the snap wins over a stray tap. */

  constructor(parent: HTMLElement, bus?: EventBus) {
    this.bus = bus ?? null;
    this.overlay = document.createElement("div");
    this.overlay.id = "pre-run-screen";
    this.injectStyles();
    parent.appendChild(this.overlay);
    this.overlay.style.display = "none";
  }

  show(
    metaState: MetaState,
    onStart: PreRunCallback,
    onBack?: PreRunBackCallback,
    onDeployStart?: () => void,
    onReset?: () => void | Promise<void>,
    onSetLanguage?: (lang: "en" | "de") => void
  ): void {
    this.onStart = onStart;
    this.onBack = onBack ?? null;
    this.onDeployStart = onDeployStart ?? null;
    this.onReset = onReset ?? null;
    this.onSetLanguage = onSetLanguage ?? null;
    this.metaStateRef = metaState;
    if (__NATIVE_SHELL__) crumb("prerun:show.start");
    this.selectedOperator = "sentinel-7";
    this.committedInitials = metaState.playerInitials ?? "";
    this.focusIndex = 0;
    this.selectedModules = [];
    this.slotEls = [];
    // Task-003 (2026-prestart-deployment-redesign, AC-10/AC-15) — seed the
    // rail's focus from the persisted `settings.threatLevel` (Spec 1's
    // "carried" field) so the rail opens where the save left off; never
    // written back here (no new save-write path — AC-19 forbids a new save
    // FIELD, and the operator selector already has this exact no-memory-
    // across-visits shape, so this stays consistent with its sibling).
    // Computed here, before buildHeroZone() below, so the hero threat
    // portrait's first paint (AC-30) already matches, not one frame stale.
    this.threatFocusIndex = Math.max(
      0,
      THREAT_LEVEL_IDS.indexOf(metaState.settings.threatLevel)
    );
    this.selectedThreatLevel = DEFAULT_THREAT_LEVEL;
    this.destroyed = false;
    this.overlay.innerHTML = "";
    this.overlay.classList.remove("prerun-shakeout");
    this.overlay.style.display = "flex";

    // Living arena atmosphere. S188 — a generated mainframe-core background plate
    // sits behind the grid/haze/vig (CSS background-image; if the asset is absent
    // the layer is simply transparent and the radial-gradient field shows — the
    // §3 graceful-degradation rail).
    this.overlay.insertAdjacentHTML(
      "beforeend",
      `<div class="prerun-atmosphere">
        <div class="prerun-bgplate"></div>
        <div class="prerun-atmo-grid"></div>
        <div class="prerun-atmo-haze"></div>
        <div class="prerun-atmo-vig"></div>
      </div>`
    );

    // S188 — gate the NEW reveal/selection/boot-in motion on the OS reduced-motion
    // signal (the decorative motion is NOT identity-defining like the CRT/glitch
    // atmosphere, which stays on regardless — see the styles note). Class-driven,
    // so the stylesheet stays free of a `prefers-reduced-motion` query (the
    // PreRun policy). Guarded for headless/jsdom where matchMedia is absent.
    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.overlay.classList.toggle("prerun-rm", reduce);
    // One-shot boot-in reveal (move 2): the panels stagger-fade on entry.
    this.overlay.classList.add("prerun-bootin");

    if (__NATIVE_SHELL__) crumb("prerun:atmosphere");

    // 2026-prestart-two-screen-delta — the pre-start is TWO acts, not one
    // screen (D-AC-1 / D-AC-2). `main` is the stage both acts play on; which
    // act is mounted into it is `this.act`. A two-stage state inside this
    // class rather than a second class: every value the two acts share
    // (`selectedOperator`, `selectedModules`, initials, `selectedThreatLevel`)
    // already lives here, so a split would be pure plumbing with a new
    // synchronisation surface for D-AC-15 to get wrong. The spec calls this a
    // reversible technical decision and leaves it open (§5).
    this.act = 1;
    this.mainEl = document.createElement("div");
    this.mainEl.className = "prerun-main";
    this.overlay.appendChild(this.mainEl);
    this.renderAct(metaState);
    if (__NATIVE_SHELL__) crumb("prerun:appended");

    // BL-130/BL-142 — the app ships at the L presentation (the S/M steps were
    // retired), and S142's L-layout is the canonical layout, so the L reflow
    // (bounded heights + internal scroll + tighter rhythm) is always engaged.
    this.overlay.classList.add("prerun-scale-l");

    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        this.handleBack();
      }
    };
    document.addEventListener("keydown", this.escHandler);

    if (__NATIVE_SHELL__) {
      // Mount is built. If the device dies AFTER this crumb (during the first
      // paint frames captured by crumbFrames), it's a native/GPU-process crash,
      // not a JS build error — see platform/diagnostics.
      crumb("prerun:show.done");
      crumbFrames("prerun");
    }

    // Richtung C — the deferred post-layout centring the carousel needed is
    // gone with the track: nothing here is positioned by measured layout, so
    // there is no second frame to wait for.
  }

  hide(): void {
    this.overlay.style.display = "none";
    this.overlay.classList.remove("prerun-shakeout");
    this.destroyed = true;
    // BL-200 — never orphan the deploy checklist across a hide.
    this.hideDeployChecklist();
    if (this.resetArmTimer !== null) {
      clearTimeout(this.resetArmTimer);
      this.resetArmTimer = null;
    }
    if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler);
      this.escHandler = null;
    }
    for (const u of this.muteUnsubs) u();
    this.muteUnsubs = [];
    // DEVICE ROUND 2026-08-18 — tear down the three centered modals (each
    // drops its own capture-phase Escape listener with it).
    this.settingsModal?.destroy();
    this.settingsModal = null;
    this.moduleModal?.destroy();
    this.moduleModal = null;
    this.idModal?.destroy();
    this.idModal = null;
    // S129 — tear down the legal overlay if it was left open.
    this.legalOverlay?.destroy();
    this.legalOverlay = null;
  }

  // ═══ THE TWO ACTS (2026-prestart-two-screen-delta, D-AC-1 / D-AC-2) ═══════

  /**
   * Mount the current act into the shared stage.
   *
   * The pre-start is one deployment sequence in two acts, split by two
   * different player decisions — "how do I go in?" and "what am I up against?"
   * — not by surface area. Richtung C's single-screen confrontation is NOT the
   * information architecture here: the approved spec replaces baseline AC-1
   * with D-AC-1 (Screen 1's four zones) and D-AC-2 (Screen 2's four zones).
   * What C contributes is its VISUAL language — a picture stage instead of
   * isolated panels, standing light, contact shadow, rim, scrims, depth — and
   * that carries onto BOTH acts. The stage does not change between them; its
   * focus does: act 1 belongs to VERA, act 2 to FENRIR.
   */
  private renderAct(metaState: MetaState): void {
    const main = this.mainEl;
    if (!main || this.destroyed) return;
    main.innerHTML = "";
    main.dataset.act = String(this.act);
    this.overlay.classList.toggle("prerun-act1", this.act === 1);
    this.overlay.classList.toggle("prerun-act2", this.act === 2);
    // Every per-act node handle is rebuilt below; drop the stale ones first so
    // a render can never write into a detached tree from the previous act.
    // The modals parent on `this.overlay` (not `main`), so they survive the
    // innerHTML wipe — destroy them explicitly; the act builders below
    // recreate the ones the act actually carries.
    this.moduleModal?.destroy();
    this.moduleModal = null;
    this.idModal?.destroy();
    this.idModal = null;
    this.slotEls = [];
    this.rotaryItems = [];
    this.rotaryArrows = [];
    this.heroCapName = null;
    this.heroCapRole = null;
    this.heroCapFx = null;
    this.heroCapLock = null;
    this.deployBtn = null;
    this.deployHint = null;
    this.busNodes = [];
    this.busTraces = [];
    this.needLine = null;
    this.idChip = null;
    this.idChipValue = null;
    this.modCountEl = null;
    this.nameField = null;

    if (this.act === 1) this.buildAct1(metaState);
    else this.buildAct2(metaState);

    this.renderIdChip();
    this.updateDeployGate();
  }

  /** Step to the other act, preserving every committed value (D-AC-15's
   *  "Rückweg ist explizit und verlustfrei"). Nothing is reset — the acts are
   *  two views of one state. */
  private goToAct(act: 1 | 2): void {
    const meta = this.metaStateRef;
    if (!meta || this.act === act) return;
    // D-AC-15 — capture BEFORE renderAct tears the current act's DOM down.
    this.committedInitials = this.getInitialsValue();
    this.act = act;
    this.renderAct(meta);
  }

  // ─── ACT 1 — BUILD YOUR OPERATOR (D-AC-1) ────────────────────────────────

  /** Four zones, in order, each with a stable anchor:
   *  header · operator hero · loadout · identity + primary action. */
  private buildAct1(metaState: MetaState): void {
    const main = this.mainEl!;
    main.appendChild(this.buildActHeader(1, metaState));
    if (__NATIVE_SHELL__) crumb("prerun:act1.header");

    main.appendChild(
      this.buildHeroStage({
        zone: "operator-hero",
        accent:
          RARITY_COLORS[OPERATOR_RARITY[this.focusedOperator]] ??
          COLORS.SENTINEL_GREEN,
        items: ALL_OPERATORS.map((op) => ({
          key: op,
          src: `/img/operators/${op}.webp`,
          accent: RARITY_COLORS[OPERATOR_RARITY[op]] ?? COLORS.SENTINEL_GREEN,
          locked: !metaState.unlockedOperators.includes(op),
        })),
        focus: this.focusIndex,
        onFocus: (i) => this.setFocus(i, metaState),
        label: t("prerun.operator"),
        reelCx: "67%",
      })
    );
    if (__NATIVE_SHELL__) crumb("prerun:act1.hero");

    main.appendChild(this.buildAct1Console(metaState));
    if (__NATIVE_SHELL__) crumb("prerun:act1.console");

    this.renderOperator(metaState);
  }

  // ─── ACT 2 — FACE THE THREAT (D-AC-2) ────────────────────────────────────

  /** Four zones: header · threat hero · threat context · summary + DEPLOY. */
  private buildAct2(metaState: MetaState): void {
    const main = this.mainEl!;
    main.appendChild(this.buildActHeader(2, metaState));
    if (__NATIVE_SHELL__) crumb("prerun:act2.header");

    main.appendChild(
      this.buildHeroStage({
        zone: "threat-hero",
        accent:
          THREAT_TIER_COLOR[
            THREAT_LEVEL_IDS[this.threatFocusIndex] ?? DEFAULT_THREAT_LEVEL
          ],
        items: THREAT_LEVEL_IDS.map((id) => ({
          key: id,
          src: this.threatAssetSrc(id),
          accent: THREAT_TIER_COLOR[id],
          locked: threatLockReason(id, metaState) !== null,
          cropY: RAIL_CROP_Y[id],
        })),
        focus: this.threatFocusIndex,
        onFocus: (i) => this.setThreatFocus(i, metaState),
        label: t("prerun.fenrir.threatLevel"),
      })
    );
    if (__NATIVE_SHELL__) crumb("prerun:act2.hero");

    main.appendChild(this.buildThreatContextZone());
    if (__NATIVE_SHELL__) crumb("prerun:act2.context");

    main.appendChild(this.buildAct2Console(metaState));
    if (__NATIVE_SHELL__) crumb("prerun:act2.console");

    this.renderThreat(metaState);
  }

  // ─── Shared: the act header (zone 1 of both) ─────────────────────────────

  /**
   * Richtung C's command line, now carrying the act. ABORT/BACK on the left,
   * the act's own name in the middle, the ⚙ on the right — one touch-high row
   * that floats over the top of the stage rather than occupying a band of its
   * own (the 393px-high profile cannot spare one).
   *
   * On act 2 the left control is the explicit, lossless way back to act 1
   * (D-AC-15); on act 1 it leaves the screen entirely.
   */
  private buildActHeader(act: 1 | 2, metaState: MetaState): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "prerun-cmd";
    bar.dataset.zone = "header";

    // DEVICE ROUND 2026-08-18 (owner): BACK moved OUT of the top bar and into
    // the console row, next to WEITER/DEPLOY — the header keeps the act title
    // and the ⚙ only. See makeConsoleBack().

    const mid = document.createElement("div");
    mid.className = "prerun-cmd-mission";
    const step = document.createElement("span");
    step.className = "prerun-act-step";
    step.textContent = String(act);
    mid.appendChild(step);
    const title = document.createElement("span");
    title.className = "prerun-act-title";
    title.textContent = act === 1 ? t("prerun.act1") : t("prerun.act2");
    mid.appendChild(title);
    bar.appendChild(mid);

    // BL-130 — the ONE ⚙; the loose controls (language · audio · legal ·
    // reset) stay folded behind it. DEVICE ROUND 2026-08-18 (owner): it opens
    // the shared CENTERED settings modal (the in-game Einstellungen shell,
    // spec 2026-pause-settings-overlays) instead of a dropdown, and the
    // button takes a full touch footprint — it read "sehr sehr klein" on
    // device. Material stays the shared gear-button sheet (BL-130 parity).
    ensurePrerunGearButtonStyles();
    const gearBtn = document.createElement("button");
    gearBtn.type = "button";
    gearBtn.id = "prerun-gear";
    gearBtn.className = "prerun-gear-btn";
    gearBtn.dataset.sfxBtn = "";
    gearBtn.textContent = "⚙";
    gearBtn.title = t("settings.title");
    gearBtn.setAttribute("aria-label", t("settings.title"));
    gearBtn.setAttribute("aria-haspopup", "dialog");
    gearBtn.addEventListener("click", () => this.openSettingsModal());
    bar.appendChild(gearBtn);
    this.buildSettingsModal(metaState);
    return bar;
  }

  /** DEVICE ROUND 2026-08-18 (owner) — settings as a centered modal on the
   *  shared shell. Content is the canonical PreRun set, unchanged in order:
   *  Language → Audio → Legal → Reset (BL-130), each row full-width so the
   *  reset control can never be clipped the way the dropdown clipped it. */
  private buildSettingsModal(metaState: MetaState): void {
    // The mute button below re-subscribes to the bus — drop stale
    // subscriptions first so an act switch can't stack them.
    for (const u of this.muteUnsubs) u();
    this.muteUnsubs = [];
    this.settingsModal?.destroy();
    this.settingsModal = new ModalShell(this.overlay, {
      id: "prerun-settings-overlay",
      panelId: "prerun-settings-card",
      panelClass: "prerun-settings-modal",
      labelledBy: "prerun-settings-title",
      onBackdropClick: () => this.closeSettingsModal(),
      onEscape: () => this.closeSettingsModal(),
    });
    this.settingsModal.append(
      modalHeader({
        icon: "⚙",
        title: t("settings.title"),
        subtitle: t("settings.subtitle"),
        titleId: "prerun-settings-title",
      }),
      modalSection(t("setting.section"))
    );
    const langRow = modalRow("prerun-set-lang");
    langRow.appendChild(this.buildLanguageButton(metaState));
    this.settingsModal.append(langRow);
    if (this.bus) {
      this.settingsModal.append(modalSection(t("hud.audio.section")));
      const audioRow = modalRow("prerun-set-audio");
      audioRow.appendChild(this.buildMuteButton(metaState));
      this.settingsModal.append(audioRow);
    }
    this.settingsModal.append(modalSection(t("legal.title")));
    const legalRow = modalRow("prerun-set-legal");
    legalRow.appendChild(this.buildLegalButton());
    this.settingsModal.append(legalRow);
    const resetRow = modalRow("prerun-set-reset");
    resetRow.appendChild(this.buildResetButton());
    this.settingsModal.append(resetRow);
    const foot = modalFooter();
    foot.appendChild(
      modalButton({
        label: t("settings.back"),
        rank: "primary",
        onActivate: () => this.closeSettingsModal(),
      })
    );
    this.settingsModal.append(foot);
  }

  private openSettingsModal(): void {
    // The three PreRun surfaces are mutually exclusive (AC-1e's spirit,
    // widened to the settings modal): opening one closes the others.
    this.closeModuleCatalog();
    this.closeIdSetter();
    this.settingsModal?.show();
  }

  private closeSettingsModal(): void {
    this.settingsModal?.hide();
  }

  /** AC-30 (KEEP) — the ONE per-tier source both act-2 surfaces read: the
   *  rotary position and the hero it becomes when focused share this exact
   *  path; only the CSS crop differs (`docs/THREAT_ART_BIBLE.md` §7). */
  private threatAssetSrc(id: ThreatLevelId): string {
    return `/img/threat/fenrir-${id}.webp`;
  }

  /** The ENEMY INTEL control and the ECHELON dossier popover it reveals — the
   *  lore stays one tap away rather than crowding the context surface. It is
   *  the ONLY disclosure surface on act 2 (D-AC-11). */
  private buildEnemyIntel(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "prerun-enemy-intel";
    const pop = this.buildFenrirDossier();
    pop.classList.add("prerun-intel-pop");
    wrap.appendChild(pop);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "prerun-intel-btn";
    btn.dataset.sfxBtn = "";
    btn.setAttribute("aria-expanded", "false");
    btn.textContent = `▤ ${t("prerun.fenrir.intel")}`;
    btn.addEventListener("click", () => {
      const open = wrap.classList.toggle("open");
      btn.setAttribute("aria-expanded", String(open));
    });
    wrap.appendChild(btn);
    return wrap;
  }

  // ─── Shared: the hero stage + rotary (D-AC-3 · D-AC-4 · D-AC-6 · D-AC-8) ──

  /**
   * ONE component, two instances — the shared selector DNA the spec requires
   * (D-AC-6: "Ein Vertrag, zwei Instanzen"). The player learns "swipe = choose"
   * on act 1 and finds exactly the same behaviour on act 2.
   *
   * WHAT THIS IS, AND WHAT IT IS NOT. It is a rotary/coverflow (D-AC-8): the
   * active figure is dominant and centred, its neighbours are pushed back,
   * scaled down, dimmed and stacked beneath it. It is NOT five equal character
   * cards in a row — there is no card at all, no frame, no per-item chrome.
   * The rotary items ARE the character art, staged on the picture stage that
   * Richtung C contributes. That distinction is the whole point: the owner
   * ruled out the flat five-card grid, and the spec's D-AC-8 replaces baseline
   * AC-16 precisely because its 12% scale cap "für eine Leiste geschrieben war
   * und ein Rotary unmöglich macht".
   *
   * D-AC-8's two-sided rule is honoured by construction below: active-to-
   * neighbour is open upward (SCALE[0]/SCALE[1] = 1.61), while neighbour-to-
   * neighbour stays calm at SCALE[1]/SCALE[2] = 1.107 — inside the ≤12% cap,
   * so the staggering never tips into a card effect.
   *
   * D-AC-3 (fixed hero geometry): the FRAME is a plain block element whose box
   * never depends on the selection; every item is absolutely positioned inside
   * it and only transformed. Nothing about focus, lock state, text length or
   * image aspect can move it.
   *
   * D-AC-4 (protection zone): the frame IS the declared protection zone
   * (`data-protect="hero"`), and no text node is ever mounted inside it — the
   * caption is a sibling below. This is also why the 3px text-stroke that
   * Richtung C needed is gone: the spec predicted it (risk 3, "fällt der Text
   * von der Figur, entfällt die Ursache"), and it did.
   */
  private buildHeroStage(cfg: {
    zone: string;
    accent: string;
    items: Array<{
      key: string;
      src: string;
      accent: string;
      locked: boolean;
      cropY?: number;
    }>;
    focus: number;
    onFocus: (index: number) => void;
    label: string;
    /** Where the reel's active position sits across the frame. Act 1 pushes it
     *  right so VERA and her identity block compose together instead of the
     *  text sitting centred under her (owner, 2026-08-15). D-AC-6 permits this
     *  — it requires a shared interaction GRAMMAR, and says in as many words
     *  that visual sameness is expressly not required. */
    reelCx?: string;
  }): HTMLElement {
    const zone = document.createElement("div");
    zone.className = "prerun-herozone";
    zone.dataset.zone = cfg.zone;

    zone.insertAdjacentHTML(
      "beforeend",
      // Depth stack, back to front. Every one of these is surface: no text, no
      // datum, nothing tappable. `aria-hidden` keeps them out of the a11y tree.
      `<div class="prerun-depth" aria-hidden="true"></div>` +
        `<div class="prerun-corelight" aria-hidden="true"></div>` +
        `<div class="prerun-rim l" aria-hidden="true"></div>` +
        `<div class="prerun-rim r" aria-hidden="true"></div>`
    );

    // The FIXED rectangle. D-AC-3 is a property of this element.
    const frame = document.createElement("div");
    frame.className = "prerun-heroframe";
    frame.style.setProperty("--reel-cx", cfg.reelCx ?? "50%");
    frame.tabIndex = 0;
    frame.setAttribute("role", "listbox");
    frame.setAttribute("aria-label", cfg.label);
    frame.style.setProperty("--hero-ac", cfg.accent);

    this.rotaryItems = cfg.items.map((item, i) => {
      const el = document.createElement("div");
      el.className = "prerun-rot";
      el.dataset.rotKey = item.key;
      el.dataset.idx = String(i);
      el.dataset.sfxBtn = "";
      el.style.setProperty("--rot-ac", item.accent);
      const back = document.createElement("span");
      back.className = "prerun-rot-back";
      back.setAttribute("aria-hidden", "true");
      el.appendChild(back);
      // DEVICE ROUND 2026-08-18 ("transparenter Skin", finding 6): the
      // runtime SVG alpha-solidifier underlay that build 106 tried here is
      // GONE — on the owner's device the filter silently did nothing (the
      // hair stayed see-through), and where it did work it backed every
      // soft anti-aliased edge with hard black. The fix moved into the ONE
      // affected asset itself: ghost.webp is baked interior-opaque
      // (owner-ordered single-asset override of the asset freeze), edge
      // anti-aliasing untouched.
      const img = document.createElement("img");
      img.className = "prerun-rot-img";
      img.src = item.src;
      // AC-30 (KEEP) — the per-tier crop anchor keeps its single documented
      // home; act 1 declares no per-item deviation, so the CSS default stands.
      if (item.cropY !== undefined) {
        img.style.objectPosition = `50% ${item.cropY}%`;
      }
      img.alt = "";
      img.setAttribute("aria-hidden", "true");
      // Graceful degradation (§3): a missing asset drops the image, not the
      // stage — the rotary keeps its position so nothing reflows.
      img.addEventListener("error", () => img.remove(), { once: true });
      el.appendChild(img);
      // ─── D-AC-4: the protection zone is the VISIBLE figure ──────────────
      // It used to be declared on `.prerun-rot` itself, and that box is not the
      // figure: it is 142 % of the frame's height, because the oversize IS the
      // crop mechanism — the figure is deliberately taller than the frame and
      // cut at the thigh. Roughly a third of the declared area is therefore
      // scissored away and invisible, and the AC's own method (compare bounding
      // rects) counted text sitting in that dead band as text "over the figure".
      // Measured 2026-08-16 at 852x393 act 2: 4 text nodes intersected the
      // active box, two of them purely through the clipped-away part.
      //
      // So the declaration moves onto a box that matches what the frame shows.
      // `--rot-s` is the position's own scale, published by applyRotary: at
      // scale 1 the frame shows 1/1.42 of the host, and a receded position is
      // scaled about its top edge so more of it fits — hence the min(). No JS
      // measurement, no paint, no hit-testing, and the rotary's own geometry is
      // untouched, which is what keeps screen 1 byte-identical.
      const prot = document.createElement("span");
      prot.className = "prerun-rot-prot";
      prot.dataset.protect = "figure";
      prot.setAttribute("aria-hidden", "true");
      el.appendChild(prot);
      // Owner finding 7 (device round 2026-08-18): the 🔒 sits ON the locked
      // figure when it is centred — the caption's lock line alone reads as
      // UI, not as the figure's state. CSS shows it for `.on.locked` only.
      const lockBadge = document.createElement("span");
      lockBadge.className = "prerun-rot-lockbadge";
      lockBadge.textContent = "🔒";
      lockBadge.setAttribute("aria-hidden", "true");
      el.appendChild(lockBadge);
      // AC-9(1) kept: a tap on a NEIGHBOUR centres it. The gate blocks the
      // commit, never the tap — so a locked item is tappable too.
      el.addEventListener("click", () => {
        if (this.suppressCardClick) return;
        cfg.onFocus(i);
      });
      frame.appendChild(el);
      return el;
    });

    // AC-9(3) kept: keyboard ←/→ on the frame.
    frame.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        cfg.onFocus(cfg.focus - 1 < 0 ? 0 : this.currentFocus() - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        cfg.onFocus(this.currentFocus() + 1);
      }
    });
    frame.insertAdjacentHTML(
      "beforeend",
      // The stage's own light and grain, and the four corner ticks that give a
      // technical frame its edges without drawing a box around it.
      `<div class="prerun-keylight" aria-hidden="true"></div>` +
        `<div class="prerun-grain" aria-hidden="true"></div>` +
        `<div class="prerun-ticks" aria-hidden="true">` +
        `<i class="tl"></i><i class="tr"></i><i class="bl"></i><i class="br"></i></div>`
    );

    // AC-9(2) kept: horizontal swipe/drag over the frame.
    this.bindRotaryDrag(frame, cfg.onFocus);

    // AC-9(4) — the ‹ › step arrows, flanking the stage at mid height. They
    // were lost when the two-act rework replaced the stepper with pips alone,
    // which silently took AC-9 from four input paths down to three.
    this.rotaryArrows = ([-1, 1] as const).map((dir) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `prerun-rot-arw ${dir < 0 ? "prev" : "next"}`;
      btn.dataset.sfxBtn = "";
      btn.setAttribute(
        "aria-label",
        t(dir < 0 ? "prerun.prevOp" : "prerun.nextOp")
      );
      btn.appendChild(document.createElement("i"));
      btn.addEventListener("click", () => {
        if (this.suppressCardClick) return;
        cfg.onFocus(this.currentFocus() + dir);
      });
      frame.appendChild(btn);
      return btn;
    });
    zone.appendChild(frame);

    // The caption lives OUTSIDE the protection zone (D-AC-4) — below the
    // frame, never over the figure.
    const cap = document.createElement("div");
    cap.className = "prerun-herocap";
    this.heroCapName = document.createElement("div");
    this.heroCapName.className = "prerun-heroname";
    cap.appendChild(this.heroCapName);
    this.heroCapRole = document.createElement("div");
    this.heroCapRole.className = "prerun-heroline";
    cap.appendChild(this.heroCapRole);
    const rule = document.createElement("div");
    rule.className = "prerun-caprule";
    cap.appendChild(rule);
    void cfg.label;
    // The effects get their own stacked lines rather than one joined run —
    // the owner asked for a typographic BLOCK, and a block is what a column of
    // short lines makes. Rebuilt per render, so the node list is held.
    this.heroCapFx = document.createElement("div");
    this.heroCapFx.className = "prerun-herofx";
    cap.appendChild(this.heroCapFx);
    this.heroCapLock = document.createElement("div");
    this.heroCapLock.className = "prerun-herolock";
    cap.appendChild(this.heroCapLock);

    // AC-9(4) kept: direct jump targets, one per option, each with a real
    // touch-floor hit area (D-AC-18) — the rotary's own positions are too
    // small to be the only jump target at the far end of the reel.
    const pips = document.createElement("div");
    pips.className = "prerun-pips";
    // The selection mark FLOWS between positions instead of switching. It is a
    // single element translated by index; the dots behind it stay put.
    const runner = document.createElement("span");
    runner.className = "prerun-piprunner";
    runner.setAttribute("aria-hidden", "true");
    pips.appendChild(runner);
    this.rotaryPips = cfg.items.map((item, i) => {
      const hit = document.createElement("button");
      hit.type = "button";
      hit.className = "prerun-pip";
      hit.dataset.idx = String(i);
      hit.dataset.sfxBtn = "";
      hit.setAttribute("aria-label", item.key);
      hit.addEventListener("click", () => {
        if (this.suppressCardClick) return;
        cfg.onFocus(i);
      });
      hit.appendChild(document.createElement("i"));
      pips.appendChild(hit);
      return hit;
    });
    frame.appendChild(pips);
    frame.appendChild(cap);
    return zone;
  }

  /** The focus index of whichever act is mounted — the two selectors share
   *  one grammar but keep their own cursor. */
  private currentFocus(): number {
    return this.act === 1 ? this.focusIndex : this.threatFocusIndex;
  }

  /**
   * D-AC-8's staggering, applied to every rotary position.
   *
   * SCALE/OPACITY/OFFSET are indexed by |distance from focus|. The ratios are
   * the AC, written down: active-to-neighbour 1.00/0.62 = 1.61 (open upward,
   * the coverflow language), neighbour-to-neighbour 0.62/0.56 = 1.107, i.e.
   * 10.7% — inside the ≤12% cap that keeps the reel calm.
   *
   * Four computed properties differ between active and neighbour (scale,
   * opacity, z-order and the drop-shadow elevation), three of them non-colour,
   * against D-AC-8's floor of two-with-one-non-colour.
   *
   * Deterministic from the index alone — no measured layout, so it behaves
   * identically in jsdom and in the browser, and D-AC-19 (no layout shift from
   * a selection change) holds by construction: only transforms change.
   */
  private applyRotary(focus: number, lockedAt: (i: number) => boolean): void {
    // Index = |distance from focus|. The last entry is the "beyond the reel"
    // slot: without it every far position collapsed onto the |d|=2 spot and
    // stacked, so two 0.24-opacity figures painted over each other and read as
    // one brighter ghost (seen at 852x393 with focus on the first option).
    // D-AC-7 only requires the IMMEDIATE neighbours to be visible and all five
    // to stay reachable and position-stable — the pips carry reachability, so
    // the far ones fade out rather than pile up.
    const SCALE = [1, 0.62, 0.56, 0.52];
    // DEVICE ROUND 2026-08-18 (owner: "linke und rechte … leicht schwächer …
    // Das überlappt aktuell sehr stark", and act 2's right-side pile-up
    // measured at 852x393: d1/d2/d3 centres 519/568/591 px, each ~160 px wide).
    // Neighbours recede harder on BOTH channels — dimmer (opacity/brightness/
    // saturation) AND further out (offset), so the active figure stands alone.
    // SCALE stays untouched: the 1.00/0.62 = 1.61 and 0.62/0.56 = 1.107
    // ratios above are the written-down AC (D-AC-8); D-AC-7 keeps holding —
    // the immediate neighbour stays clearly visible at 0.5.
    const OPACITY = [1, 0.5, 0.14, 0];
    const OFFSET = [0, 40, 57, 66];
    // Atmosphere per distance, not opacity alone. A receding position loses
    // focus, colour and light the way something further away in a lit room
    // does — which is the difference between a rotary of POSITIONS and a
    // stack of transparent copies of the same PNG. D-AC-8's measured
    // properties (scale, opacity, z-order, elevation) are untouched by this;
    // these are additional, and they are set from the index so they stay
    // deterministic and jsdom-safe.
    const BLUR = ["0px", "1px", "2.8px", "3.4px"];
    const SAT = [1, 0.62, 0.36, 0.3];
    const BRI = [1, 0.64, 0.42, 0.38];
    this.rotaryItems.forEach((el, i) => {
      const d = i - focus;
      const a = Math.min(Math.abs(d), SCALE.length - 1);
      // A faded-out position must not eat taps meant for the stage behind it.
      el.style.pointerEvents = OPACITY[a] === 0 ? "none" : "auto";
      const dir = d < 0 ? -1 : 1;
      el.style.transform = `translateX(calc(-50% + ${
        dir * OFFSET[a]!
      }%)) scale(${SCALE[a]})`;
      el.style.opacity = String(OPACITY[a]);
      el.style.zIndex = String(100 - a * 10);
      el.style.setProperty("--rot-blur", BLUR[a]!);
      // Owner finding 7 (device round 2026-08-18): a LOCKED figure on stage
      // is veiled, never in full clarity — the lock has to read on the figure
      // itself, not only in the caption line. CSS pairs this with the
      // `.prerun-rot-lockbadge` 🔒 shown for `.on.locked`.
      const activeLocked = a === 0 && lockedAt(i);
      el.style.setProperty(
        "--rot-sat",
        String(activeLocked ? 0.45 : SAT[a])
      );
      el.style.setProperty(
        "--rot-bri",
        String(activeLocked ? 0.52 : BRI[a])
      );
      // Published for the protection box only (D-AC-4): the box has to know how
      // much of its host the frame actually shows, and that depends on the
      // scale this position is drawn at. See `.prerun-rot-prot`.
      el.style.setProperty("--rot-s", String(SCALE[a]));
      el.classList.toggle("on", a === 0);
      // The IMMEDIATE neighbour is marked so it can carry its own light. Only
      // distance 1 — the further positions keep the plain recession ramp.
      el.classList.toggle("nxt", a === 1);
      el.classList.toggle("locked", lockedAt(i));
      el.setAttribute("aria-selected", String(a === 0));
    });
    this.rotaryPips.forEach((pip, i) => {
      pip.classList.toggle("on", i === focus);
      pip.classList.toggle("lk", lockedAt(i));
    });
    const pipRail = this.rotaryPips[0]?.parentElement;
    if (pipRail) pipRail.style.setProperty("--pip-i", String(focus));
    const frameEl = this.rotaryItems[0]?.parentElement;
    if (frameEl) {
      // The stage light drifts a little with the selection — it settles slower
      // than the figure does, which is what reads as a camera and a lamp
      // rather than a swap. Centred on the middle position so the drift is
      // symmetric across the reel.
      const mid = (this.rotaryItems.length - 1) / 2;
      frameEl.style.setProperty(
        "--reel-drift",
        `${((focus - mid) * 1.6).toFixed(2)}%`
      );
    }
    for (const arw of this.rotaryArrows) {
      const prev = arw.classList.contains("prev");
      arw.classList.toggle(
        "off",
        prev ? focus === 0 : focus === this.rotaryItems.length - 1
      );
    }
  }

  /**
   * AC-9(2) — the horizontal swipe that steps the reel, shared by both acts
   * (D-AC-6: same gesture expectation on both screens).
   *
   * Pointer capture is taken LATE, at the 4px threshold, never on pointerdown.
   * Capturing on an ANCESTOR retargets the subsequent `click` to the capturing
   * element, so a plain tap on a rotary position would never reach its own
   * handler. jsdom implements no pointer capture at all, so every unit test
   * passes either way and the screen is simply dead to taps in a real browser
   * — reproduced 2026-08-15 in Chromium at 852×393 via elementFromPoint plus a
   * real click: the element WAS the hit target and its handler never ran.
   */
  private bindRotaryDrag(
    surface: HTMLElement,
    onFocus: (index: number) => void
  ): void {
    let active = false;
    let startX = 0;
    let moved = false;
    let lastX = 0;
    let vx = 0;
    let pointerId = -1;
    const onDown = (e: PointerEvent): void => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      active = true;
      moved = false;
      this.suppressCardClick = false;
      startX = e.clientX;
      lastX = e.clientX;
      vx = 0;
      pointerId = e.pointerId;
    };
    const onMove = (e: PointerEvent): void => {
      if (!active) return;
      vx = e.clientX - lastX;
      lastX = e.clientX;
      if (Math.abs(e.clientX - startX) <= 4) return;
      if (!moved) {
        moved = true;
        try {
          surface.setPointerCapture(e.pointerId);
        } catch {
          /* not all environments support capture (jsdom) */
        }
      }
    };
    const onUp = (e: PointerEvent): void => {
      if (!active) return;
      active = false;
      if (moved) {
        try {
          surface.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
      }
      const dx = e.clientX - startX;
      if (moved) this.suppressCardClick = true;
      if (!moved) return;
      // BL-222's momentum, kept: a fast flick throws several positions, a slow
      // drag steps one. DEVICE ROUND 2026-08-18 (owner: "das scrollen geht
      // aktuell auch nur durch die kleinen Pfeile"): the pitch was a THIRD of
      // the frame (~280 px at 852 wide) — longer than a natural phone swipe,
      // so most real swipes rounded to zero and the reel felt dead. A sixth
      // matches the thumb's actual travel, and a moved-but-sub-pitch swipe
      // still steps ONE position: a gesture that visibly moved must never do
      // nothing.
      const MOMENTUM = 8;
      const MAX_THROW = 4;
      const step = Math.max(48, surface.clientWidth / 6 || 0);
      let delta = Math.round((-dx - vx * MOMENTUM) / step);
      delta = Math.max(-MAX_THROW, Math.min(MAX_THROW, delta));
      if (delta === 0 && Math.abs(dx) > 40) delta = dx < 0 ? 1 : -1;
      if (delta !== 0) onFocus(this.currentFocus() + delta);
    };
    surface.addEventListener("pointerdown", onDown);
    surface.addEventListener("pointermove", onMove);
    surface.addEventListener("pointerup", onUp);
    surface.addEventListener("pointercancel", onUp);
  }

  // ─── Zone 4: Console — identity · slots · deploy (Task-001, AC-1d) ───────

  // ─── ACT 1 zone 3 — LOADOUT (D-AC-1) ─────────────────────────────────────

  /** The three persistent slots and the ONE entry point into the module
   *  catalog (D-AC-9 — the property AC-3's label prohibition was actually
   *  after). An empty slot IS the opener; there is no second control. */
  /** The reference's small "— LABEL" section caption. It is what makes the
   *  merged console read as ONE instrument with named sections instead of a
   *  row of unlabelled boxes. */
  private consoleLabel(text: string): HTMLElement {
    const el = document.createElement("div");
    el.className = "prerun-grouplab";
    el.textContent = text;
    return el;
  }

  private buildLoadoutZone(metaState: MetaState): HTMLElement {
    const zone = document.createElement("div");
    zone.className = "prerun-loadout";
    zone.dataset.zone = "loadout";
    zone.appendChild(this.consoleLabel(t("prerun.loadout")));
    zone.appendChild(this.buildConsoleSlots(metaState));
    // The module counter from the reference. It states the committed count
    // against the required three — a real, live-measured number, not a
    // difficulty claim, and it is the same value act 2's summary mirrors.
    this.modCountEl = document.createElement("div");
    this.modCountEl.className = "prerun-modcount";
    zone.appendChild(this.modCountEl);
    return zone;
  }

  // ─── ACT 1 zone 4 — IDENTITY + WEITER (D-AC-1 · D-AC-12 · D-AC-13/14) ────

  /**
   * The Operator-ID and act 1's primary action.
   *
   * D-AC-14(a) — WEITER IS NOT THE DEPLOY GATE. It opens act 2 as soon as a
   * PLAYABLE operator is committed; an incomplete loadout does not block it.
   * That is the owner's decision of 2026-08-15 closing this spec's only open
   * question, and it is why this button reads `focusedLocked` alone and never
   * the module count. Act 2 then shows the incomplete state honestly and
   * offers the way back (D-AC-14(c)/(d)).
   */
  private buildAct1Console(metaState: MetaState): HTMLElement {
    // ONE deployment console, not three boxes with dead space between them.
    // Loadout and identity+action keep their own stable anchors (D-AC-1 asks
    // for four zones in order, each anchored — it does not ask for four
    // separate panels), and they now sit in one bordered band so the bottom of
    // the screen reads as a single instrument.
    const panel = this.makePanel(null);
    panel.classList.add("prerun-console");
    const body = panel.querySelector(".prerun-panel__body")!;
    body.classList.add("prerun-console-body");

    const row = document.createElement("div");
    row.className = "prerun-console-row";
    row.appendChild(this.buildLoadoutZone(metaState));

    const group0 = document.createElement("div");
    group0.className = "prerun-console-identity-zone";
    group0.dataset.zone = "console";
    group0.appendChild(this.consoleLabel(t("prerun.operatorId")));
    group0.appendChild(this.buildConsoleIdentity(metaState));
    row.appendChild(group0);

    this.advanceHint = document.createElement("div");
    this.advanceHint.className = "prerun-deployhint prerun-advance-hint";
    this.advanceHint.setAttribute("role", "status");
    this.advanceHint.setAttribute("aria-live", "polite");
    row.appendChild(this.advanceHint);

    const group = document.createElement("div");
    group.className = "prerun-console-deploy";
    group.dataset.console = "advance";
    const ctaRow = document.createElement("div");
    ctaRow.className = "prerun-cta-row";
    ctaRow.appendChild(this.makeConsoleBack());
    this.advanceBtn = document.createElement("button");
    this.advanceBtn.type = "button";
    this.advanceBtn.className = "prerun-deploy-btn prerun-advance-btn";
    this.advanceBtn.dataset.sfxBtn = "";
    const inner = document.createElement("span");
    inner.className = "prerun-deploy-inner";
    const main = document.createElement("span");
    main.className = "prerun-deploy-main";
    main.textContent = `${t("prerun.advance")} ▷`;
    inner.appendChild(main);
    this.advanceBtn.appendChild(inner);
    this.advanceBtn.addEventListener("click", () => {
      if (this.focusedLocked) return;
      this.goToAct(2);
    });
    ctaRow.appendChild(this.advanceBtn);
    group.appendChild(ctaRow);
    row.appendChild(group);

    body.appendChild(row);
    return panel;
  }

  // ─── ACT 2 zone 3 — THREAT CONTEXT (D-AC-2 · D-AC-10) ───────────────────

  /**
   * The tier's own context surface.
   *
   * D-AC-10 is the binding constraint and it is why this zone is nearly empty:
   * no element may claim a property that differs MECHANICALLY between tiers —
   * no enemy density, no AI adaptation, no resource pressure, no reward, no
   * difficulty, no number and no comparative word. In phase 1 the five tiers
   * are provably identical, so a line like "enemy density: medium" would be
   * the deleted 9·7·9·8 bars returning in prettier clothes.
   *
   * Atmospheric fiction IS allowed — but open question 2 of the spec ("does
   * the threat context surface get text at all, and who writes it?") is
   * unanswered, and the spec says explicitly: without an answer the surface
   * stays empty. So what it carries is the tier's NAME, its lock reason where
   * one applies, and the ECHELON dossier one tap away. Nothing invented.
   */
  private buildThreatContextZone(): HTMLElement {
    const panel = this.makePanel(null);
    panel.classList.add("prerun-threatctx");
    panel.dataset.zone = "threat-context";
    const body = panel.querySelector(".prerun-panel__body")!;
    body.classList.add("prerun-threatctx-body");

    // No tier name and no lock line here: the hero caption two rows up already
    // says both, and repeating them was the first thing the 852x393 render
    // showed (2026-08-15). What is left is deliberately sparse — see the
    // doc-block above for why inventing per-tier prose is not this pass's call.
    const spacer = document.createElement("div");
    spacer.className = "prerun-console-spacer";
    body.appendChild(spacer);

    body.appendChild(this.buildEnemyIntel());
    return panel;
  }

  // ─── ACT 2 zone 4 — SUMMARY + DEPLOY (D-AC-2 · D-AC-12 · D-AC-15) ───────

  /**
   * The read-only mirror of act 1 plus the final gate.
   *
   * D-AC-15 — this shows exactly the committed operator variant, the module
   * IDs and the Operator-ID, and carries NO control that could change any of
   * them. The way back to act 1 is the header's own back control, explicit and
   * lossless.
   *
   * D-AC-14(c)/(d) — the summary is honest while incomplete (`2 / 3`), and
   * when DEPLOY is locked the readiness bus's need line names the reason.
   */
  private buildAct2Console(metaState: MetaState): HTMLElement {
    const panel = this.makePanel(null);
    panel.classList.add("prerun-console");
    panel.dataset.zone = "console";
    const body = panel.querySelector(".prerun-panel__body")!;
    body.classList.add("prerun-console-body");

    const row = document.createElement("div");
    row.className = "prerun-console-row";

    // ONE readout, not two. The first build stacked a readiness bus (INITIALS ·
    // AI MODEL · MODULES) over a summary (AI MODEL · MODULES · OPERATOR ID) —
    // the same three things named twice in one band, which the 852x393 render
    // made obvious. Each cell now carries its own state pip, its label and its
    // committed VALUE, so D-AC-14's per-condition readiness and D-AC-15's
    // read-only mirror live in one element instead of competing for the band.
    const readout = document.createElement("div");
    readout.className = "prerun-readout";
    readout.dataset.console = "summary";
    this.busNodes = [];
    this.busTraces = [];
    this.summaryEls = [];
    const labels = [
      t("prerun.deploy.step.initials"),
      t("prerun.deploy.step.model"),
      t("prerun.deploy.step.modules"),
    ];
    labels.forEach((label, i) => {
      if (i > 0) {
        const trace = document.createElement("div");
        trace.className = "prerun-bus-trace";
        trace.appendChild(document.createElement("i"));
        this.busTraces.push(trace);
        readout.appendChild(trace);
      }
      const cell = document.createElement("div");
      cell.className = "prerun-bus-node prerun-ro-cell";
      const head = document.createElement("div");
      head.className = "prerun-ro-head";
      const pip = document.createElement("span");
      pip.className = "prerun-bus-pip";
      head.appendChild(pip);
      const lb = document.createElement("span");
      lb.className = "prerun-bus-lb";
      lb.textContent = label;
      head.appendChild(lb);
      cell.appendChild(head);
      const val = document.createElement("div");
      val.className = "prerun-sum-v";
      cell.appendChild(val);
      this.summaryEls.push(val);
      this.busNodes.push(cell);
      readout.appendChild(cell);
    });
    const last = document.createElement("div");
    last.className = "prerun-bus-trace out";
    last.appendChild(document.createElement("i"));
    this.busTraces.push(last);
    readout.appendChild(last);
    row.appendChild(readout);

    this.needLine = document.createElement("div");
    this.needLine.className = "prerun-needline";
    this.needLine.setAttribute("role", "status");
    this.needLine.setAttribute("aria-live", "polite");

    row.appendChild(this.buildConsoleDeploy());
    body.appendChild(row);
    body.appendChild(this.needLine);
    void metaState;
    return panel;
  }

  /** Console group 1/3 — the AC-6 Operator-ID CHIP (compact; the old
   *  1fr-1fr-with-DEPLOY box is gone) and the AC-22 setter surface it opens.
   *  Task-005 (2026-prestart-deployment-redesign). */
  private buildConsoleIdentity(metaState: MetaState): HTMLElement {
    const group = document.createElement("div");
    group.className = "prerun-console-identity";
    group.dataset.console = "identity";

    group.appendChild(this.buildIdChip());
    // The setter modal parents itself on `this.overlay` (centered shell) —
    // nothing to append into the console group.
    this.buildIdSetter(metaState);
    // NOTE: the chip stays at its placeholder here — `renderIdChip()` reads
    // cells via `this.overlay` (getInitialsValue), which is still a detached
    // tree mid-build. `show()` calls `renderIdChip()` again once mounted.
    return group;
  }

  /** The AC-6 chip: icon + label + the CURRENT 3-letter callsign, kept live
   *  by `renderIdChip`. A tap opens the setter (AC-22) — and, AC-1(e),
   *  closes the module catalog first, so the two possible surfaces on this
   *  screen are mutually exclusive. */
  private buildIdChip(): HTMLElement {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "prerun-id-chip";
    chip.dataset.sfxBtn = "";
    chip.setAttribute("aria-label", t("prerun.operatorId"));
    // The group caption above already names this control, so the chip carries
    // the VALUE, not a second label. Crest + value + edit affordance, with the
    // "tap to change" hint under it — the reference's shape.
    const crest = document.createElement("span");
    crest.className = "prerun-id-crest";
    crest.textContent = "⛨";
    chip.appendChild(crest);
    const val = document.createElement("span");
    val.className = "prerun-id-chip-val";
    chip.appendChild(val);
    this.idChipValue = val;
    const pen = document.createElement("span");
    pen.className = "prerun-id-pen";
    pen.textContent = "✎";
    pen.setAttribute("aria-hidden", "true");
    chip.appendChild(pen);
    const hint = document.createElement("span");
    hint.className = "prerun-id-chip-lab";
    hint.textContent = t("prerun.tapToChange");
    chip.appendChild(hint);
    chip.addEventListener("click", () => this.toggleIdSetter());
    this.idChip = chip;
    return chip;
  }

  /** The AC-22 setter surface — DEVICE ROUND 2026-08-18 (owner): a CENTERED
   *  MODAL on the shared shell ("wie bei den Einstellungen und Pause im
   *  Spiel"), replacing the cramped drop-up. Content and order unchanged:
   *  the privacy notice — permanently visible the instant the setter opens,
   *  no hover/click needed, and BEFORE the input in DOM order (AC-22) — then
   *  the unchanged 3-cell callsign input (`buildNameInput`,
   *  `src/ui/PreRunScreenInput.ts`; iOS's keyboard-free tap-to-cycle cells
   *  stay exactly as shipped, D-126's charset stays locked; the modal fill
   *  gives the cells a LARGER touch footprint), then DONE. The modal DOM
   *  stays mounted while hidden, so the deploy gate can always read the
   *  cells regardless of the toggle. */
  private buildIdSetter(metaState: MetaState): void {
    this.idModal?.destroy();
    this.idModal = new ModalShell(this.overlay, {
      id: "prerun-id-overlay",
      panelId: "prerun-id-card",
      panelClass: "prerun-id-modal",
      labelledBy: "prerun-id-title",
      onBackdropClick: () => this.closeIdSetter(),
      onEscape: () => this.closeIdSetter(),
    });
    // AC-1(e) — the disclosure-surface marker the exclusivity contract counts.
    this.idModal.root.dataset.surface = "id-setter";
    this.idModal.append(
      modalHeader({
        icon: "⛨",
        title: t("prerun.operatorId"),
        // Native (iOS) uses tap-to-cycle cells, not a keyboard — say so
        // explicitly (the ▲▼ chevrons reinforce it); web says "enter".
        subtitle: __NATIVE_SHELL__
          ? t("prerun.tapInitials")
          : t("prerun.enterInitials"),
        titleId: "prerun-id-title",
      })
    );

    const privacy = document.createElement("div");
    privacy.className = "prerun-id-privacy";
    const notice = document.createElement("p");
    notice.className = "prerun-id-privacy-notice";
    const locale = getLocale() === "de" ? "de" : "en";
    notice.textContent = SUBMIT_PRIVACY_NOTICE[locale];
    privacy.appendChild(notice);
    const link = document.createElement("button");
    link.type = "button";
    link.className = "prerun-privacy-link";
    link.dataset.sfxBtn = "";
    link.textContent = t("prerun.privacyNotice.more");
    link.addEventListener("click", () => this.openLegal("privacy"));
    privacy.appendChild(link);
    this.idModal.append(privacy);

    const idbox = document.createElement("div");
    idbox.className = "prerun-idbox";
    const idLabel = document.createElement("span");
    idLabel.className = "prerun-callsign-lab";
    const idIcon = document.createElement("img");
    idIcon.className = "prerun-id-ic";
    idIcon.src = "/img/prerun/icon-operator.webp";
    idIcon.alt = "";
    idIcon.setAttribute("aria-hidden", "true");
    idIcon.addEventListener("error", () => idIcon.remove(), { once: true });
    idLabel.appendChild(idIcon);
    idLabel.appendChild(document.createTextNode(t("prerun.operatorId")));
    buildNameInput(idbox, idLabel, metaState, {
      onInput: () => {
        // D-AC-15 — the value the step to act 2 will carry.
        this.committedInitials = this.getInitialsValue();
        this.renderIdChip();
        this.updateDeployGate();
      },
    });
    this.nameField = idbox.querySelector(".prerun-namefield");
    this.idModal.append(idbox);

    const foot = modalFooter();
    foot.appendChild(
      modalButton({
        label: t("prerun.loadout.done"),
        rank: "primary",
        onActivate: () => this.closeIdSetter(),
      })
    );
    this.idModal.append(foot);
  }

  /** Refresh the chip's displayed callsign from the live cells — called on
   *  build and on every keystroke/tap, so the chip can never show a stale
   *  value. A blank field (a first-time web player before their first
   *  keystroke) reads a neutral placeholder rather than an empty chip. */
  private renderIdChip(): void {
    if (!this.idChipValue) return;
    const raw = this.getInitialsValue();
    this.idChipValue.textContent = raw.trim().length > 0 ? raw : "---";
    this.idChip?.classList.toggle("empty", raw.trim().length === 0);
  }

  private toggleIdSetter(): void {
    if (this.idModal?.visible) {
      this.closeIdSetter();
    } else {
      this.openIdSetter();
    }
  }

  /** AC-1(e) — opening the setter closes the other surfaces first: the
   *  possible surfaces on this screen are mutually exclusive, so a test
   *  counting simultaneously-open surfaces never finds more than one. */
  private openIdSetter(): void {
    this.closeModuleCatalog();
    this.closeSettingsModal();
    this.idModal?.show();
  }

  private closeIdSetter(): void {
    this.idModal?.hide();
  }

  /** Console group 2/3 — the three loadout slots. An EMPTY slot is the ONLY
   *  entry into the module catalog (AC-3, the separate `+ MODUL` button is
   *  gone); a FILLED slot shows its module and a tap clears it (parity with
   *  the catalog tile's own deselect-on-click). */
  private buildConsoleSlots(metaState: MetaState): HTMLElement {
    const group = document.createElement("div");
    group.className = "prerun-console-slots";
    group.dataset.console = "slots";

    const row = document.createElement("div");
    row.className = "prerun-slotrow";
    for (let i = 0; i < 3; i++) {
      const tile = document.createElement("div");
      tile.className = "prerun-slot";
      tile.dataset.slotIndex = String(i);
      tile.dataset.sfxBtn = "";
      tile.addEventListener("click", () => this.handleSlotTap(i));
      this.slotEls.push(tile);
      row.appendChild(tile);
    }
    group.appendChild(row);
    this.renderSlots();

    // The catalog modal parents itself on `this.overlay` (centered shell).
    this.buildModuleCatalog(metaState);
    return group;
  }

  /** Console group 3/3 — DEPLOY (glowing active / dark inactive) + the inline
   *  hint explaining why it's locked. */
  /* The standalone readiness bus is gone: act 2's readout now carries the same
     three nodes together with their committed values, so the screen names each
     condition once instead of twice. What the bus was FOR is unchanged and
     still holds — each node is one gate condition, the traces carry power only
     as far as the first unmet one, and the last trace reads `g.valid` itself,
     so "power reaches DEPLOY" and "DEPLOY is armed" remain the same expression
     rather than two that have to be kept in agreement. That equivalence was
     the whole point of replacing the old percentage bar (AC-7: no step may be
     permanently satisfied without ever having measured anything). */

  private buildConsoleDeploy(): HTMLElement {
    const group = document.createElement("div");
    group.className = "prerun-console-deploy";
    group.dataset.console = "deploy";

    this.deployBtn = document.createElement("button");
    this.deployBtn.type = "button";
    this.deployBtn.className = "prerun-deploy-btn";
    this.deployBtn.dataset.sfxBtn = "";
    const inner = document.createElement("span");
    inner.className = "prerun-deploy-inner";
    const main = document.createElement("span");
    main.className = "prerun-deploy-main";
    // AC-32 (Task-007) — the SAME text in DE and EN (an established loanword,
    // owner decision 2026-08-14), and NO explanatory sub-line: the old
    // localized ".prerun-deploy-sub" ("INITIALIZE DEFENSE PROTOCOL" /
    // "VERTEIDIGUNGSPROTOKOLL INITIALISIEREN") is gone — it weakened the
    // primary CTA's hierarchy and wasn't needed for comprehension or layout.
    main.textContent = "▷ DEPLOY";
    inner.appendChild(main);
    this.deployBtn.appendChild(inner);
    this.deployBtn.addEventListener("click", () => this.handleDeploy());
    const showTip = (): void => this.showDeployChecklist();
    const hideTip = (): void => this.hideDeployChecklist();
    this.deployBtn.addEventListener("mouseenter", showTip);
    this.deployBtn.addEventListener("focus", showTip);
    this.deployBtn.addEventListener("touchstart", showTip, { passive: true });
    this.deployBtn.addEventListener("mouseleave", hideTip);
    this.deployBtn.addEventListener("blur", hideTip);
    const ctaRow = document.createElement("div");
    ctaRow.className = "prerun-cta-row";
    ctaRow.appendChild(this.makeConsoleBack());
    ctaRow.appendChild(this.deployBtn);
    group.appendChild(ctaRow);

    // NO SECOND HINT LINE. `.prerun-deployhint` used to repeat, directly under
    // the button, what the readiness bus's need line already says directly
    // above it — the same sentence twice, three lines apart (seen in the
    // 852x393 render, 2026-08-15). Richtung C's rule is ONE line that names
    // the break; the bus owns it. The node is kept (unrendered) so
    // `updateDeployGate` stays a single code path and the BL-200 checklist
    // tooltip, which carries the per-step detail, is unaffected.
    this.deployHint = document.createElement("div");
    this.deployHint.className = "prerun-deployhint";
    this.deployHint.hidden = true;
    group.appendChild(this.deployHint);

    return group;
  }

  /**
   * The ONE back action (device round 2026-08-18, owner): act 2 steps back to
   * act 1 (lossless, D-AC-15); act 1 leaves to the TITLE. Wired to the
   * console BACK buttons (next to WEITER/DEPLOY) and to Escape.
   */
  private handleBack(): void {
    if (this.act === 2) {
      this.goToAct(1);
      return;
    }
    this.hide();
    this.onBack?.();
  }

  /** The console-row BACK control — lives beside the act's primary CTA
   *  (owner directive: bottom, next to WEITER/DEPLOY, never in the header). */
  private makeConsoleBack(): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "prerun-cmd-back prerun-console-back";
    btn.dataset.sfxBtn = "";
    btn.textContent = `‹ ${t("settings.back")}`;
    btn.addEventListener("click", () => this.handleBack());
    return btn;
  }

  /** A tap on a loadout slot (AC-3): empty → open the module catalog (the
   *  only entry point); filled → clear that pick (mirrors the catalog tile's
   *  own click-to-deselect). */
  private handleSlotTap(index: number): void {
    const modId = this.selectedModules[index];
    if (modId) {
      this.toggleModule(modId);
    } else {
      this.openModuleCatalog();
    }
  }

  private openModuleCatalog(): void {
    // AC-1(e) — mutual exclusion with the other surfaces (see openIdSetter).
    this.closeIdSetter();
    this.closeSettingsModal();
    this.moduleModal?.show();
  }

  private closeModuleCatalog(): void {
    this.moduleModal?.hide();
  }

  /** (Re)paint the three slot tiles from `selectedModules` — index-aligned,
   *  so slot 0 always shows the first pick. Called on every module toggle. */
  private renderSlots(): void {
    const modules = getAllModules();
    this.slotEls.forEach((tile, i) => {
      const modId = this.selectedModules[i];
      tile.replaceChildren();
      tile.classList.toggle("empty", !modId);
      tile.classList.toggle("full", !!modId);
      if (modId) {
        tile.appendChild(this.makeModuleIcon(modId, "prerun-slot-ic"));
        const nm = document.createElement("span");
        nm.className = "prerun-slot-name";
        const mod = modules.find((m: AIModule) => m.id === modId);
        nm.textContent = mod ? t(mod.name) : "";
        tile.appendChild(nm);
      } else {
        const plus = document.createElement("span");
        plus.className = "prerun-slot-plus";
        plus.textContent = "+";
        tile.appendChild(plus);
        const lbl = document.createElement("span");
        lbl.className = "prerun-slot-label";
        lbl.textContent = t("prerun.loadout.empty");
        tile.appendChild(lbl);
      }
    });
  }

  // ─── LOADOUT MODULES — the module catalog (Task-001, AC-3) ───────────────

  /** The module catalog — the ONE disclosure surface a loadout slot opens
   *  (AC-3). DEVICE ROUND 2026-08-18 (owner): a CENTERED MODAL on the shared
   *  shell ("wie bei den Einstellungen und Pause im Spiel") — the drop-up
   *  read as "seltsam gelöst" and its tiles as dead on device. Tile content
   *  is unchanged (S188); the modal DOM stays mounted while hidden, so the
   *  tiles stay reachable by test/automation regardless of the toggle. */
  private buildModuleCatalog(metaState: MetaState): void {
    this.moduleModal?.destroy();
    this.moduleModal = new ModalShell(this.overlay, {
      id: "prerun-mod-overlay",
      panelId: "prerun-mod-card",
      panelClass: "prerun-mod-modal",
      labelledBy: "prerun-mod-title",
      onBackdropClick: () => this.closeModuleCatalog(),
      onEscape: () => this.closeModuleCatalog(),
    });
    // AC-1(e)/AC-4 (Task-006) — the disclosure-surface marker: a test counts
    // simultaneously-open `[data-surface]` elements and never finds more
    // than one (mutually exclusive with `data-surface="id-setter"`, see
    // openModuleCatalog/openIdSetter).
    this.moduleModal.root.dataset.surface = "catalog";
    const modules = getAllModules();

    this.moduleModal.append(
      modalHeader({
        icon: "⬡",
        title: t("prerun.modules"),
        subtitle: t("prerun.selectModules"),
        titleId: "prerun-mod-title",
      })
    );

    // Owner: a horizontal scroller — ~5 premium cards visible, swipe/scroll for
    // the rest (we ship 12). Each card: hex icon · name · category · description,
    // a ✓ when picked, a padlock when locked.
    const grid = document.createElement("div");
    grid.className = "prerun-modules";
    for (const mod of modules) {
      const tile = document.createElement("div");
      tile.className = "prerun-modc";
      tile.dataset.module = mod.id;
      tile.dataset.sfxBtn = "";
      const hex = document.createElement("div");
      hex.className = "modc-hex";
      // S188 — a glanceable module icon (webp; degrades to nothing on error).
      hex.appendChild(this.makeModuleIcon(mod.id, "modc-ic"));
      tile.appendChild(hex);
      const txt = document.createElement("div");
      txt.className = "modc-txt";
      const nameEl = document.createElement("div");
      nameEl.className = "modc-name";
      nameEl.textContent = t(mod.name);
      txt.appendChild(nameEl);
      const catEl = document.createElement("div");
      catEl.className = "modc-cat";
      catEl.textContent = t(
        `prerun.modcat.${MODULE_CATEGORY[mod.effect.type] ?? "system"}`
      );
      txt.appendChild(catEl);
      const descEl = document.createElement("div");
      descEl.className = "modc-desc";
      descEl.textContent = t(mod.description);
      txt.appendChild(descEl);
      tile.appendChild(txt);
      // The ✓ check (selected) — toggled in renderModuleSelection.
      const check = document.createElement("div");
      check.className = "modc-check";
      check.textContent = "✓";
      tile.appendChild(check);
      const unlocked = metaState.unlockedModules.includes(mod.id);
      if (!unlocked) {
        tile.classList.add("locked");
        const lock = document.createElement("div");
        lock.className = "modc-lock";
        lock.textContent = "🔒";
        hex.appendChild(lock);
        tile.title = t("prerun.locked");
      } else {
        tile.addEventListener("click", () => this.toggleModule(mod.id));
      }
      grid.appendChild(tile);
    }
    this.moduleModal.append(grid);

    // "X / 3 MODULES SELECTED" — bottom-centred (owner's mockup).
    const counter = document.createElement("div");
    counter.className = "prerun-mod-count";
    counter.textContent = `0 / 3 ${t("prerun.modulesSelected")}`;
    this.moduleModal.append(counter);

    const foot = modalFooter();
    foot.appendChild(
      modalButton({
        label: t("prerun.loadout.done"),
        rank: "primary",
        onActivate: () => this.closeModuleCatalog(),
      })
    );
    this.moduleModal.append(foot);
  }

  /**
   * (Re)apply the operator's focus state across the stage. Richtung C — this
   * replaces `renderCarousel` plus its whole coverflow rig (`targetTxForFocus`
   * / `applyCoverflow` / `centerCarousel` / `animateToFocus`, ~120 lines of
   * measured-layout tweening that existed to scale five cards as they passed a
   * viewport centre). There is no track to slide and no cards to scale: the
   * figure swaps, the block re-reads, the pips re-mark.
   *
   * The focus/commit split it drives is UNCHANGED and stays the contract:
   * `focusedOperator` is whatever is on stage (locked or not), `focusedLocked`
   * lets the Deploy gate explain itself, and `selectedOperator` only ever
   * follows an UNLOCKED figure — the gate blocks the commit, never the step.
   */
  private renderOperator(metaState: MetaState): void {
    const op = ALL_OPERATORS[this.focusIndex];
    if (!op) return;
    this.focusedOperator = op;
    this.focusedLocked = !metaState.unlockedOperators.includes(op);
    if (!this.focusedLocked) this.selectedOperator = op;

    this.applyRotary(this.focusIndex, (i) => {
      const o = ALL_OPERATORS[i];
      return !!o && !metaState.unlockedOperators.includes(o);
    });

    if (this.heroCapName) {
      this.heroCapName.textContent = "";
      const crest = document.createElement("span");
      crest.className = "prerun-crest";
      crest.textContent = OPERATOR_CREST[op] ?? "◆";
      this.heroCapName.appendChild(crest);
      this.heroCapName.appendChild(document.createTextNode(OPERATOR_NAMES[op]));
      const ver = OPERATOR_VERSIONS[op];
      if (ver) {
        const v = document.createElement("span");
        v.className = "prerun-herover";
        v.textContent = ver;
        this.heroCapName.appendChild(v);
      }
      this.heroCapName.style.setProperty(
        "--rar-color",
        RARITY_COLORS[OPERATOR_RARITY[op]] ?? COLORS.SENTINEL_GREEN
      );
    }
    if (this.heroCapRole) this.heroCapRole.textContent = t(`prerun.role.${op}`);
    if (this.heroCapFx) {
      // Same source the old card's two stat boxes read (`operator.<id>.desc`),
      // one line per effect. Nothing invented, nothing lost — the chrome is
      // gone and the column is what carries the structure now.
      this.heroCapFx.textContent = "";
      const desc = t(`operator.${op}.desc`, undefined, OPERATOR_DESC[op] ?? "");
      for (const part of desc
        .split("|")
        .map((x) => x.trim())
        .filter(Boolean)) {
        const line = document.createElement("div");
        line.className = "prerun-fxline";
        line.textContent = part;
        this.heroCapFx.appendChild(line);
      }
    }
    if (this.heroCapLock) {
      if (this.focusedLocked) {
        const threshold = getOperatorUnlockThreshold(op);
        const runs = totalRunsFrom(metaState);
        const progress = Math.min(Math.max(runs - 1, 0), threshold);
        this.heroCapLock.textContent =
          `🔒 ${t("prerun.lock.title")} · ` +
          t("prerun.lock.note", { n: Math.max(threshold - progress, 0) });
        this.heroCapLock.removeAttribute("aria-hidden");
      } else {
        this.heroCapLock.textContent = "\u00a0";
        this.heroCapLock.setAttribute("aria-hidden", "true");
      }
    }
    const zone = this.mainEl?.querySelector<HTMLElement>(
      '[data-zone="operator-hero"]'
    );
    zone
      ?.querySelector<HTMLElement>(".prerun-heroframe")
      ?.style.setProperty(
        "--hero-ac",
        RARITY_COLORS[OPERATOR_RARITY[op]] ?? COLORS.SENTINEL_GREEN
      );
    this.updateDeployGate();
  }

  /**
   * Act 2's counterpart to `renderOperator` — the same shape, deliberately.
   * D-AC-6 asks for one interaction grammar across both selectors, and the
   * cheapest way to keep that true over time is for the two render paths to be
   * readable side by side rather than to share an over-parametrised one.
   */
  private renderThreat(metaState: MetaState): void {
    const id = THREAT_LEVEL_IDS[this.threatFocusIndex];
    if (!id) return;
    const lockReason = threatLockReason(id, metaState);
    if (!lockReason) this.selectedThreatLevel = id;

    this.applyRotary(this.threatFocusIndex, (i) => {
      const t2 = THREAT_LEVEL_IDS[i];
      return !!t2 && threatLockReason(t2, metaState) !== null;
    });

    if (this.heroCapName) {
      this.heroCapName.textContent = t(`threatLevel.${id}`);
      this.heroCapName.style.setProperty("--rar-color", THREAT_TIER_COLOR[id]);
      this.heroCapName.classList.toggle("locked", !!lockReason);
    }
    if (this.heroCapRole) this.heroCapRole.textContent = "FENRIR";
    if (this.heroCapFx) this.heroCapFx.textContent = "";
    if (this.heroCapLock) {
      if (lockReason) {
        // AC-20 kept: two lock languages, never swapped. `release` (sandbox
        // only) never carries a run count; `runs` always does, templated here
        // rather than stored with a digit baked in (AC-21a).
        this.heroCapLock.textContent =
          "🔒 " +
          (lockReason === "release"
            ? t("threatLevel.lock.release")
            : t("threatLevel.lock.runs", {
                n: threatRunsRemaining(id, metaState),
              }));
        this.heroCapLock.removeAttribute("aria-hidden");
      } else {
        this.heroCapLock.textContent = "\u00a0";
        this.heroCapLock.setAttribute("aria-hidden", "true");
      }
    }
    const zone = this.mainEl?.querySelector<HTMLElement>(
      '[data-zone="threat-hero"]'
    );
    zone
      ?.querySelector<HTMLElement>(".prerun-heroframe")
      ?.style.setProperty("--hero-ac", THREAT_TIER_COLOR[id]);
    if (this.mainEl) {
      this.mainEl.style.setProperty("--tier-ac", THREAT_TIER_COLOR[id]);
      const esc = this.threatFocusIndex / (THREAT_LEVEL_IDS.length - 1);
      this.mainEl.style.setProperty("--esc", esc.toFixed(3));
    }
    this.updateDeployGate();
  }

  /** Centre an exact threat index (clamped, no wrap) — mirrors `setFocus`. */
  private setThreatFocus(index: number, metaState: MetaState): void {
    const n = THREAT_LEVEL_IDS.length;
    this.threatFocusIndex = Math.min(Math.max(index, 0), n - 1);
    this.renderThreat(metaState);
  }

  /** Centre an exact index (clamped, no wrap) — the swipe-snap and the pips
   *  share this. BL-218. */
  private setFocus(index: number, metaState: MetaState): void {
    const n = ALL_OPERATORS.length;
    this.focusIndex = Math.min(Math.max(index, 0), n - 1);
    this.renderOperator(metaState);
  }

  /** A module icon `<img>` (the S188 webp glyph family); removes itself on error
   *  so a missing icon degrades to the text-only tile/slot (graceful, §3). */
  private makeModuleIcon(id: ModuleId, className: string): HTMLImageElement {
    const img = document.createElement("img");
    img.className = className;
    img.src = `/img/modules/${id}.webp`;
    img.alt = "";
    img.setAttribute("aria-hidden", "true");
    img.addEventListener("error", () => img.remove(), { once: true });
    return img;
  }

  private toggleModule(id: ModuleId): void {
    const idx = this.selectedModules.indexOf(id);
    if (idx >= 0) {
      this.selectedModules.splice(idx, 1);
    } else {
      // Pick up to 3 — a 4th pick is rejected (deselect one first).
      if (this.selectedModules.length >= 3) {
        const counter =
          this.overlay.querySelector<HTMLElement>(".prerun-mod-count");
        if (counter) {
          counter.classList.remove("full-flash");
          // Force a reflow so the flash animation restarts on every rejection.
          void counter.offsetWidth;
          counter.classList.add("full-flash");
        }
        return;
      }
      this.selectedModules.push(id);
    }
    this.renderModuleSelection();
  }

  private renderModuleSelection(): void {
    const tiles = this.overlay.querySelectorAll<HTMLElement>(".prerun-modc");
    for (const tile of tiles) {
      const id = tile.dataset.module as ModuleId;
      // `.sel` lights the card (border/glow) + reveals the ✓ check (CSS).
      tile.classList.toggle("sel", this.selectedModules.includes(id));
    }
    const counter =
      this.overlay.querySelector<HTMLElement>(".prerun-mod-count");
    if (counter) {
      const n = this.selectedModules.length;
      counter.textContent = `${n} / 3 ${t("prerun.modulesSelected")}`;
      counter.classList.toggle("active", n > 0);
      counter.classList.toggle("full", n === 3);
    }
    // Task-001 — the console's 3 loadout slots mirror the same picks live.
    this.renderSlots();
    // BL-127 — modules feed the deploy gate; refresh it live as picks toggle.
    this.updateDeployGate();
  }

  // ─── Panel builder ───────────────────────────────────────────────────────

  private makePanel(
    title: string | null,
    opts?: { crimson?: boolean; gold?: boolean; livetag?: string }
  ): HTMLElement {
    const panel = document.createElement("div");
    panel.className = `prerun-panel${opts?.crimson ? " crimson" : ""}`;

    // Corner etch (shared BoardChrome vocabulary) — the top corner-etches FLANK
    // a panel header, so a header-less panel (the OPERATOR/LOADOUT panel built
    // with makePanel(null)) skips them: bare floating top etches with no header
    // read as a stray "hidden frame" (BL-130). The scan overlay still applies.
    const etchColor = opts?.crimson ? COLORS.ORACLE_RED : UI_CHROME.etchAccent;
    if (title) {
      panel.insertAdjacentHTML(
        "beforeend",
        `<span class="prerun-etch t l" style="--etch-ac:${etchColor}"></span>` +
          `<span class="prerun-etch t r" style="--etch-ac:${etchColor}"></span>`
      );
    }
    panel.insertAdjacentHTML("beforeend", `<div class="prerun-scan"></div>`);

    if (title) {
      const header = document.createElement("div");
      header.className = `prerun-panel__header${opts?.gold ? " gold" : ""}`;
      const dot = document.createElement("span");
      dot.className = "prerun-panel__dot";
      header.appendChild(dot);
      header.appendChild(document.createTextNode(title));
      if (opts?.livetag) {
        const tag = document.createElement("span");
        tag.className = "prerun-panel__livetag";
        tag.innerHTML = `<i></i>${opts.livetag}`;
        header.appendChild(tag);
      }
      panel.appendChild(header);
    }

    const body = document.createElement("div");
    body.className = "prerun-panel__body";
    panel.appendChild(body);
    return panel;
  }

  // ─── Operator selection ──────────────────────────────────────────────────

  private selectOperator(op: OperatorId, metaState: MetaState): void {
    const idx = ALL_OPERATORS.indexOf(op);
    if (idx >= 0) this.focusIndex = idx;
    // S188 — centring the carousel on the card makes it the live selection.
    this.renderOperator(metaState);
  }

  // ─── Deploy gate (G3) ───────────────────────────────────────────────────

  private getInitialsValue(): string {
    // Read via readCellChar so this works for BOTH cell shapes: <input>.value
    // (keyboard / web) and <div>.textContent (native iOS tap-to-cycle). Reading
    // `.value` directly would always be "" on native → deploy locked forever.
    const cells =
      this.overlay.querySelectorAll<HTMLElement>(".prerun-namecell");
    // D-AC-15 — the cells are ACT 1's; act 2 mirrors them read-only and mounts
    // no editable copy, so on act 2 there is nothing to read and this used to
    // return "". That is not "no initials", it is the value having been thrown
    // away by the step: the readout showed "– – –" and DEPLOY stayed locked
    // forever, which made act 2 a dead end for every player. Found by the
    // two-screen test migration on 2026-08-16 (OperatorStepper's deploy path);
    // it reproduces in the browser, where act 2's first cell reads "– – –" even
    // with a persisted ID. So the committed value outlives the act that edits
    // it, and the cells stay the live truth wherever they exist.
    if (cells.length === 0) return this.committedInitials;
    return Array.from(cells).map(readCellChar).join("");
  }

  /** The deploy-gate state — the ONE source the lock, the inline hint and the
   *  BL-200 checklist tooltip all read, so they can never disagree. The AI model
   *  defaults to `sentinel-7` and so is always satisfied (it gates nothing) — it
   *  is surfaced in the checklist only to complete the player's "3 steps" model. */
  private deployGateState(): {
    initials: string;
    initialsValid: boolean;
    modulesValid: boolean;
    moduleCount: number;
    operatorUnlocked: boolean;
    valid: boolean;
  } {
    const initials = this.getInitialsValue();
    const initialsValid = initials.trim().length >= 1;
    // BL-127 — modules are mandatory: the deploy lock is the AND of both gates.
    const modulesValid = this.selectedModules.length === 3;
    // BL-218 — a locked operator may be centred for preview, but it cannot
    // deploy: the centred card must be unlocked.
    const operatorUnlocked = !this.focusedLocked;
    return {
      initials,
      initialsValid,
      modulesValid,
      moduleCount: this.selectedModules.length,
      operatorUnlocked,
      valid: initialsValid && modulesValid && operatorUnlocked,
    };
  }

  private updateDeployGate(): void {
    const g = this.deployGateState();
    if (this.deployBtn) {
      this.deployBtn.classList.toggle("locked", !g.valid);
    }
    if (this.nameField) {
      this.nameField.classList.toggle("invalid", !g.initialsValid);
    }
    // D-AC-14(a) — WEITER IS NOT THE DEPLOY GATE. Act 1's primary action needs
    // exactly one thing: a PLAYABLE operator committed. An incomplete loadout
    // does not block it; act 2 then states the shortfall honestly and offers
    // the way back (D-AC-14(c)/(d)). This is the owner's 2026-08-15 decision
    // closing the spec's only open question, and it is why this reads
    // `operatorUnlocked` alone and never `g.valid`.
    if (this.advanceBtn) {
      this.advanceBtn.classList.toggle("locked", !g.operatorUnlocked);
    }
    if (this.advanceHint) {
      this.advanceHint.textContent = g.operatorUnlocked
        ? "\u00a0"
        : `⚠ ${t("prerun.operatorLocked")} — ${t(
            "prerun.deploy.need.operator",
            { n: getOperatorUnlockThreshold(this.focusedOperator) }
          )}`;
      this.advanceHint.classList.toggle("blank", g.operatorUnlocked);
    }
    // D-AC-15 — act 2's read-only mirror of what act 1 committed. The module
    // cell states the real count even when short (D-AC-14(c): "2 / 3"), which
    // is the honest-while-incomplete rule; `short` marks it without claiming
    // anything is ready.
    if (this.modCountEl) {
      this.modCountEl.textContent = `${g.moduleCount} / 3 ${t(
        "prerun.deploy.step.modules"
      )}`;
      this.modCountEl.classList.toggle("short", !g.modulesValid);
    }
    if (this.summaryEls.length === 3) {
      // Cell order follows the readiness order the checklist already uses:
      // (1) initials (2) operator (3) modules.
      this.summaryEls[0]!.textContent = g.initialsValid
        ? sanitizeInitials(g.initials)
        : "– – –";
      this.summaryEls[0]!.classList.toggle("short", !g.initialsValid);
      this.summaryEls[1]!.textContent = OPERATOR_NAMES[this.selectedOperator];
      this.summaryEls[1]!.classList.toggle("short", !g.operatorUnlocked);
      const mods = getAllModules();
      const names = this.selectedModules.map((id) => {
        const m = mods.find((x: AIModule) => x.id === id);
        return m ? t(m.name) : id;
      });
      while (names.length < 3) names.push("–");
      this.summaryEls[2]!.textContent = names.join("  ·  ");
      this.summaryEls[2]!.classList.toggle("short", !g.modulesValid);
    }
    // Richtung C — the readiness bus. Each node IS one gate condition, in the
    // order the checklist already uses (① initials ② operator ③ modules), and
    // each trace carries power only if everything upstream of it holds. So the
    // circuit goes dark at exactly the first unmet condition — the player sees
    // WHERE the break is, not how far along they are.
    //
    // The bar this replaced could read full while DEPLOY was locked (it keyed
    // off a re-derived subset of the gate). That class of bug is structurally
    // gone: the last trace reads `g.valid` itself, the same value the button's
    // own lock reads, so "power reaches DEPLOY" and "DEPLOY is armed" are the
    // same expression rather than two that have to be kept in agreement.
    const conditions = [g.initialsValid, g.operatorUnlocked, g.modulesValid];
    this.busNodes.forEach((node, i) => {
      node.classList.toggle("on", conditions[i] === true);
    });
    this.busTraces.forEach((trace, i) => {
      // Trace i sits after node i, so it conducts iff every node up to and
      // including i is satisfied. The last one is `g.valid` by construction.
      const upstream = conditions.slice(0, i + 1).every(Boolean);
      trace.classList.toggle("on", upstream);
    });
    if (this.needLine) {
      // ONE line, same precedence as the inline hint above it: the on-stage
      // locked operator leads (it is the direct result of the player's own
      // last gesture), then ① initials, then ③ modules.
      this.needLine.textContent = !g.operatorUnlocked
        ? t("prerun.deploy.need.operator", {
            n: getOperatorUnlockThreshold(this.focusedOperator),
          })
        : !g.initialsValid
        ? t("prerun.deploy.need.initials")
        : !g.modulesValid
        ? t("prerun.deploy.need.modules", { n: g.moduleCount })
        : t("prerun.deploy.ready");
      this.needLine.classList.toggle("ok", g.valid);
    }
    // Update grant name in echo
    if (this.grantName) {
      this.grantName.textContent = g.initialsValid ? g.initials : "—";
    }
    // BL-200 — keep an open checklist tooltip live as gates toggle.
    if (this.deployTipOpen) this.showDeployChecklist();
  }

  // ─── BL-200 — Deploy readiness checklist tooltip ─────────────────────────

  /** Build the checklist model from the live gate state: ① initials ② AI model
   *  (always ✓ — shows the chosen model) ③ modules; a "ready" line when all done.
   *  EN + DE via `prerun.deploy.*` (the modules step reuses the SELECT-3 copy). */
  private deployChecklistModel(): import("./InfoTooltip.ts").InfoTooltipModel {
    const g = this.deployGateState();
    const ok = "✓";
    const no = "✗";
    const initialsLine = g.initialsValid
      ? `${ok} ① ${t("prerun.deploy.step.initials")} — ${sanitizeInitials(
          g.initials
        )}`
      : `${no} ① ${t("prerun.deploy.step.initials")} — ${t(
          "prerun.deploy.need.initials"
        )}`;
    // BL-218 — step ② now genuinely gates: a centred LOCKED operator shows ✗ with
    // its unlock threshold; an unlocked centred operator shows ✓ with its name.
    const modelLine = g.operatorUnlocked
      ? `${ok} ② ${t("prerun.deploy.step.model")} — ${
          OPERATOR_NAMES[this.selectedOperator]
        }`
      : `${no} ② ${t("prerun.deploy.step.model")} — ${t(
          "prerun.deploy.need.operator",
          { n: getOperatorUnlockThreshold(this.focusedOperator) }
        )}`;
    const modulesLine = g.modulesValid
      ? `${ok} ③ ${t("prerun.deploy.step.modules")} — 3/3`
      : `${no} ③ ${t("prerun.deploy.step.modules")} — ${t(
          "prerun.deploy.need.modules",
          { n: g.moduleCount }
        )}`;
    return {
      name: t("prerun.deploy.checklist"),
      accent: g.valid ? COLORS.SENTINEL_GREEN : COLORS.AMBER_WARN,
      body: [initialsLine, modelLine, modulesLine],
      // When everything is satisfied, the prominent line confirms readiness.
      meta: g.valid ? t("prerun.deploy.ready") : undefined,
    };
  }

  private showDeployChecklist(): void {
    if (!this.deployBtn) return;
    this.deployTipOpen = true;
    // Anchor LEFT of the button: the deploy console sits at the right edge of the
    // stage, so an above/left-clamped card reads without clipping (InfoTooltip
    // also hard-clamps on-stage as a backstop).
    this.deployTip.show(this.deployBtn, this.deployChecklistModel(), "left");
  }

  private hideDeployChecklist(): void {
    this.deployTipOpen = false;
    this.deployTip.hide();
  }

  // (The gear DROPDOWN and its row/section builders are retired — the ⚙ now
  // opens `buildSettingsModal`'s centered shell; BL-142's canonical control
  // order Language → Audio → Legal → Reset lives there.)

  /** S129/D-227 — the LEGAL button. Opens the in-app legal/privacy surface,
   *  reusing the mute/lang top-bar chrome so it reads as one control group. */
  private buildLegalButton(): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "prerun-lang-btn";
    btn.textContent = `⚖ ${t("legal.open")}`;
    btn.title = t("legal.title");
    btn.setAttribute("aria-label", t("legal.title"));
    btn.addEventListener("click", () => this.openLegal("privacy"));
    return btn;
  }

  /** Lazily build + show the legal overlay (one instance, reused). */
  private openLegal(
    tab: "privacy" | "impressum" | "eula" | "attributions"
  ): void {
    // B31: der Rechtstext-Korpus (legalContent + attributions, ~18 kB roh)
    // wird erst hier geholt, nicht beim Boot. Signatur bleibt synchron, damit
    // alle Aufrufer unveraendert bleiben.
    void (async () => {
      if (!this.legalOverlay) {
        const { LegalOverlay } = await import("./LegalOverlay.ts");
        this.legalOverlay = new LegalOverlay();
      }
      this.legalOverlay.show(tab);
    })();
  }

  /** BL-202 — FENRIR's THREAT DOSSIER: the antagonist brief that fills FENRIR's
   *  box (the owner's "just black" void). A tight definition list — the wolf,
   *  ECHELON's herald, who commands every hostile process on the board and runs
   *  the hostile broadcast (STORY_BIBLE §2). EN+DE; reads on the 12px `micro`
   *  floor via `.prerun-dossier` so it can never go sub-floor. */
  private buildFenrirDossier(): HTMLElement {
    const dl = document.createElement("dl");
    dl.className = "prerun-dossier";
    const rows: Array<[string, string, boolean]> = [
      [t("prerun.fenrir.class"), t("prerun.fenrir.classV"), false],
      [t("prerun.fenrir.command"), t("prerun.fenrir.commandV"), false],
      [t("prerun.fenrir.signal"), t("prerun.fenrir.signalV"), true],
    ];
    for (const [k, v, threat] of rows) {
      const dt = document.createElement("dt");
      dt.textContent = k;
      const dd = document.createElement("dd");
      if (threat) dd.className = "threat";
      dd.textContent = v;
      dl.appendChild(dt);
      dl.appendChild(dd);
    }
    return dl;
  }

  /** BL-115 — the EN/DE locale toggle. Click flips the locale: it persists +
   *  applies via `onSetLanguage` (wired in main.ts), then re-renders this screen
   *  so every label lights up in the new language. Reuses the mute-button chrome
   *  so it reads as one with the top-bar controls. */
  private buildLanguageButton(metaState: MetaState): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "prerun-lang-btn";
    const current = metaState.settings.language;
    btn.textContent = `🌐 ${current.toUpperCase()}`;
    btn.title = t("setting.language");
    btn.setAttribute("aria-label", `${t("setting.language")}: ${current}`);
    btn.addEventListener("click", () => {
      const next = metaState.settings.language === "de" ? "en" : "de";
      this.onSetLanguage?.(next);
      this.rerenderForLanguage();
    });
    return btn;
  }

  /** BL-115 — rebuild the screen in the active locale after a language toggle.
   *  A full re-show (the screen rebuilds cleanly on `show()`); the operator +
   *  module + threat-rail picks are preserved across the flip so the player
   *  loses no progress. Goes via `hide()` first so the prior key/mute
   *  listeners are torn down (no duplicate-listener leak). */
  private rerenderForLanguage(): void {
    if (!this.metaStateRef || !this.onStart) return;
    const op = this.selectedOperator;
    const mods = [...this.selectedModules];
    // Task-003 (2026-prestart-deployment-redesign) — capture the COMMITTED
    // rail pick the same way `op` above captures the committed operator
    // (not the raw focus index — `show()` below reseeds focus from the
    // persisted `settings.threatLevel` regardless).
    const threatLevel = this.selectedThreatLevel;
    const meta = this.metaStateRef;
    this.hide();
    this.show(
      meta,
      this.onStart,
      this.onBack ?? undefined,
      this.onDeployStart ?? undefined,
      this.onReset ?? undefined,
      this.onSetLanguage ?? undefined
    );
    // Restore the in-progress loadout (show() resets it to defaults).
    if (meta.unlockedOperators.includes(op)) {
      this.selectOperator(op, meta);
    }
    if (!threatLockReason(threatLevel, meta)) {
      this.setThreatFocus(THREAT_LEVEL_IDS.indexOf(threatLevel), meta);
    }
    this.selectedModules = mods;
    this.renderModuleSelection();
  }

  /** A small danger control that wipes the persisted meta save. Two-step: the
   *  first click ARMS (and shows "CONFIRM WIPE?"); a second click within the
   *  window fires onReset. Auto-disarms after a few seconds. */
  private buildResetButton(): HTMLElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "prerun-reset-btn";
    const IDLE = t("prerun.reset");
    const ARMED = t("prerun.resetConfirm");
    btn.textContent = IDLE;

    const disarm = (): void => {
      btn.classList.remove("armed");
      btn.textContent = IDLE;
      if (this.resetArmTimer !== null) {
        clearTimeout(this.resetArmTimer);
        this.resetArmTimer = null;
      }
    };

    btn.addEventListener("click", () => {
      if (!btn.classList.contains("armed")) {
        btn.classList.add("armed");
        btn.textContent = ARMED;
        this.resetArmTimer = window.setTimeout(disarm, 4000);
        return;
      }
      disarm();
      void this.onReset?.();
    });

    return btn;
  }

  private buildMuteButton(metaState: MetaState): HTMLElement {
    const bus = this.bus!;
    this.musicMuted = metaState.settings.musicMuted;
    this.sfxMuted = metaState.settings.sfxMuted;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "prerun-mute-btn";
    btn.textContent = `♪ ${t("audio.audio")}`;

    const render = (): void => {
      const muted = this.musicMuted && this.sfxMuted;
      btn.classList.toggle("muted", muted);
      btn.setAttribute("aria-pressed", String(muted));
      btn.setAttribute("aria-label", t(muted ? "audio.unmute" : "audio.mute"));
    };
    render();

    btn.addEventListener("click", () => {
      const muted = this.musicMuted && this.sfxMuted;
      if (this.musicMuted === muted) bus.emit("audio.toggleMusic");
      if (this.sfxMuted === muted) bus.emit("audio.toggleMute");
    });

    this.muteUnsubs = [
      bus.on("audio.musicMuteChanged", (m: unknown) => {
        this.musicMuted = m as boolean;
        render();
      }),
      bus.on("audio.sfxMuteChanged", (m: unknown) => {
        this.sfxMuted = m as boolean;
        render();
      }),
    ];

    return btn;
  }

  // ─── Deploy ──────────────────────────────────────────────────────────────

  private handleDeploy(): void {
    // G3: gated until valid initials
    if (this.deployBtn?.classList.contains("locked")) return;

    this.onDeployStart?.();

    const glitch = document.createElement("div");
    glitch.className = "prerun-deploy-glitch";
    this.overlay.appendChild(glitch);
    this.overlay.classList.add("prerun-shakeout");

    setTimeout(() => {
      glitch.classList.add("prerun-blackout");
    }, 200);
    setTimeout(() => {
      this.hide();
      this.onStart?.({
        operator: this.selectedOperator,
        modules: [...this.selectedModules],
        // Task-003 (2026-prestart-deployment-redesign) — the threat rail's
        // COMMITTED tier (mirrors `operator: this.selectedOperator` above).
        // Supersedes Task-002's (2026-threat-profile-model, AC-8/AC-12)
        // "always the persisted default, no in-screen control" — that was
        // Phase 1; this spec adds the rail Phase 1 explicitly deferred.
        threatLevel: this.selectedThreatLevel,
      });
    }, 800);
  }

  private injectStyles(): void {
    if (document.getElementById("prerun-styles")) return;
    const style = document.createElement("style");
    style.id = "prerun-styles";
    style.textContent = getPreRunStyles();
    document.head.appendChild(style);
  }
}

/** The ⚙ BUTTON material only (BL-130 parity with the Boot/HUD gears) — the
 *  dropdown half of the gear sheet is not injected because PreRun no longer
 *  has a dropdown: the button opens the centered settings modal. Same style-id
 *  the old full sheet used, so the two can never coexist. */
function ensurePrerunGearButtonStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById("prerun-gear-styles")) return;
  const style = document.createElement("style");
  style.id = "prerun-gear-styles";
  style.textContent = gearMenuStyles("prerun", {
    fullButton: true,
    buttonOnly: true,
  });
  document.head.appendChild(style);
}
